import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import '../styles/test-system.css';
import './WorkTrial.css';
import API from '../api/api';
import Navigation from '../components/Navigation';
import GuidedTour from '../components/GuidedTour';
import {
    formatInTimeZone,
    formatToLocalDateTime,
    getTimeZoneLabel,
    parseUTCToLocal
} from '../utils/dateUtils';
import { isAuthenticated } from '../utils/auth';
import { renderTextWithLinks } from '../utils/linkify';
import { getTrialFraming, getTrialProgress, isCycleTrial } from '../utils/trialFraming';
import { getMyEnrolments } from '../api/testSystem';
import Countdown, { useNow } from '../components/tests/Countdown';
import ChatMessages from '../components/chat/ChatMessages';
import MessageComposer from '../components/chat/MessageComposer';
import NewMessagesPill from '../components/chat/NewMessagesPill';

// Firebase imports - using existing config
import app, { auth } from '../firebase/config';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// Initialize Firestore using existing Firebase app
const db = getFirestore(app);

// Generate a unique session ID for this browser tab/session
// This helps differentiate between multiple tabs or browser sessions
const generateSessionId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const tabId = Math.random().toString(36).substring(2, 9);
    return `session_${timestamp}_${random}_${tabId}`;
};

// Store session ID in a ref so it persists for the lifetime of this component instance
const SESSION_ID = generateSessionId();

const svgProps = { width: '1em', height: '1em', viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' };
const svgPropsBlock = { ...svgProps, width: '1.5em', height: '1.5em', style: { display: 'block', margin: '0 auto' } };
const strokeProps = { stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' };

const FolderIcon = ({ block }) => (
    <svg {...(block ? svgPropsBlock : svgProps)} style={block ? svgPropsBlock.style : { verticalAlign: '-0.125em' }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" {...strokeProps} />
    </svg>
);

const WarningIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" {...strokeProps} />
        <line x1="12" y1="9" x2="12" y2="13" {...strokeProps} />
        <line x1="12" y1="17" x2="12.01" y2="17" {...strokeProps} />
    </svg>
);

const LinkIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" {...strokeProps} />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" {...strokeProps} />
    </svg>
);

const CheckIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <polyline points="20 6 9 17 4 12" {...strokeProps} />
    </svg>
);

const EditIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" {...strokeProps} />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" {...strokeProps} />
    </svg>
);

const EyeIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" {...strokeProps} />
        <circle cx="12" cy="12" r="3" {...strokeProps} />
    </svg>
);

const BlockIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <circle cx="12" cy="12" r="10" {...strokeProps} />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" {...strokeProps} />
    </svg>
);

const DocumentIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" {...strokeProps} />
        <polyline points="14 2 14 8 20 8" {...strokeProps} />
        <line x1="16" y1="13" x2="8" y2="13" {...strokeProps} />
        <line x1="16" y1="17" x2="8" y2="17" {...strokeProps} />
        <polyline points="10 9 9 9 8 9" {...strokeProps} />
    </svg>
);

const SpreadsheetIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" {...strokeProps} />
        <line x1="3" y1="9" x2="21" y2="9" {...strokeProps} />
        <line x1="3" y1="15" x2="21" y2="15" {...strokeProps} />
        <line x1="9" y1="3" x2="9" y2="21" {...strokeProps} />
        <line x1="15" y1="3" x2="15" y2="21" {...strokeProps} />
    </svg>
);

const PresentationIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" {...strokeProps} />
        <line x1="8" y1="21" x2="16" y2="21" {...strokeProps} />
        <line x1="12" y1="17" x2="12" y2="21" {...strokeProps} />
    </svg>
);

const ImageFileIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" {...strokeProps} />
        <circle cx="8.5" cy="8.5" r="1.5" {...strokeProps} />
        <polyline points="21 15 16 10 5 21" {...strokeProps} />
    </svg>
);

const BookIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" {...strokeProps} />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" {...strokeProps} />
    </svg>
);

const PaperclipIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" {...strokeProps} />
    </svg>
);

const RefreshIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <polyline points="23 4 23 10 17 10" {...strokeProps} />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" {...strokeProps} />
    </svg>
);

const QuestionIcon = ({ block }) => (
    <svg {...(block ? svgPropsBlock : svgProps)} style={block ? svgPropsBlock.style : { verticalAlign: '-0.125em' }}>
        <circle cx="12" cy="12" r="10" {...strokeProps} />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" {...strokeProps} />
        <line x1="12" y1="17" x2="12.01" y2="17" {...strokeProps} />
    </svg>
);

const SoundOnIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" {...strokeProps} />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" {...strokeProps} />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" {...strokeProps} />
    </svg>
);

const SoundOffIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" {...strokeProps} />
        <line x1="23" y1="9" x2="17" y2="15" {...strokeProps} />
        <line x1="17" y1="9" x2="23" y2="15" {...strokeProps} />
    </svg>
);

const BellIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" {...strokeProps} />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" {...strokeProps} />
    </svg>
);

const BellOffIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M13.73 21a2 2 0 0 1-3.46 0" {...strokeProps} />
        <path d="M18.63 13A17.89 17.89 0 0 1 18 8" {...strokeProps} />
        <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" {...strokeProps} />
        <path d="M18 8a6 6 0 0 0-9.33-5" {...strokeProps} />
        <line x1="1" y1="1" x2="23" y2="23" {...strokeProps} />
    </svg>
);

const GroupIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" {...strokeProps} />
        <circle cx="9" cy="7" r="4" {...strokeProps} />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" {...strokeProps} />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" {...strokeProps} />
    </svg>
);


const RobotIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <rect x="3" y="11" width="18" height="10" rx="2" {...strokeProps} />
        <circle cx="12" cy="5" r="2" {...strokeProps} />
        <path d="M12 7v4" {...strokeProps} />
        <line x1="8" y1="16" x2="8" y2="16" {...strokeProps} strokeWidth="3" />
        <line x1="16" y1="16" x2="16" y2="16" {...strokeProps} strokeWidth="3" />
    </svg>
);


const UploadIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" {...strokeProps} />
        <polyline points="17 8 12 3 7 8" {...strokeProps} />
        <line x1="12" y1="3" x2="12" y2="15" {...strokeProps} />
    </svg>
);

const CloseIcon = () => (
    <svg {...svgProps} style={{ display: 'block' }}>
        <line x1="18" y1="6" x2="6" y2="18" {...strokeProps} />
        <line x1="6" y1="6" x2="18" y2="18" {...strokeProps} />
    </svg>
);

const ChevronDownIcon = () => (
    <svg {...svgProps} style={{ display: 'block' }} width="0.75em" height="0.75em">
        <polyline points="6 9 12 15 18 9" {...strokeProps} />
    </svg>
);

const DotsIcon = () => (
    <svg {...svgProps} style={{ display: 'block' }}>
        <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
        <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </svg>
);

const InfoIcon = () => (
    <svg {...svgProps} style={{ verticalAlign: '-0.125em' }}>
        <circle cx="12" cy="12" r="10" {...strokeProps} />
        <line x1="12" y1="16" x2="12" y2="12" {...strokeProps} />
        <line x1="12" y1="8" x2="12.01" y2="8" {...strokeProps} />
    </svg>
);

// Status chip: colored dot + small-caps word. One vocabulary for every list row
// and dossier header on the page.
const StatusMark = ({ status }) => (
    <span className={`wt-status wt-status--${status.color}`}>
        <span className="wt-status-dot" aria-hidden="true" />
        {status.text}
    </span>
);

// Accessible modal shell shared by every dialog on the page: focus trap,
// Escape + overlay dismissal (suppressed while busy), focus restore on close.
const WtModal = ({ titleId, title, subtitle, onClose, closeDisabled, footer, children }) => {
    const dialogRef = useRef(null);
    // The trap effect must run exactly once per modal lifetime — re-running it
    // steals focus from inputs on every parent re-render — so changing props
    // are read through refs.
    const onCloseRef = useRef(onClose);
    const closeDisabledRef = useRef(closeDisabled);
    useEffect(() => {
        onCloseRef.current = onClose;
        closeDisabledRef.current = closeDisabled;
    });

    useEffect(() => {
        const node = dialogRef.current;
        const previouslyFocused = document.activeElement;
        // Respect autoFocus inputs inside the dialog; otherwise focus the shell
        if (node && !node.contains(document.activeElement)) node.focus();

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (!closeDisabledRef.current) onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab' || !node) return;
            const focusables = node.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusables.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (active === node || !node.contains(active)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus?.();
        };
    }, []);

    return (
        <div
            className="wt-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !closeDisabled) onClose();
            }}
        >
            <div
                ref={dialogRef}
                className="wt-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
            >
                <div className="wt-modal-head">
                    <div className="wt-modal-heading">
                        <h2 className="wt-modal-title" id={titleId}>{title}</h2>
                        {subtitle && <p className="wt-modal-subtitle">{subtitle}</p>}
                    </div>
                    <button
                        type="button"
                        className="wt-icon-btn"
                        onClick={onClose}
                        disabled={closeDisabled}
                        aria-label="Close dialog"
                    >
                        <CloseIcon />
                    </button>
                </div>
                <div className="wt-modal-body">{children}</div>
                {footer && <div className="wt-modal-foot">{footer}</div>}
            </div>
        </div>
    );
};

/**
 * The window a cycle trial runs in: when it started, when it ends, and how much
 * of it is gone. Times are read in the cycle's own timezone so every candidate
 * on the cycle sees the same wall clock. It keeps its own clock rather than
 * taking one from the page, so a ticking countdown never re-renders the trial.
 */
const CycleTimeline = ({ workTrial, timeZone }) => {
    const now = useNow(30000);
    const progress = getTrialProgress(workTrial, now);
    const startedAt = workTrial?.started_at;
    const endsAt = workTrial?.ends_at;

    if (!startedAt && !endsAt) return null;

    return (
        <div className="wt-timeline" data-testid="wt-cycle-timeline">
            {progress !== null && (
                <div className="wt-timeline-track" aria-hidden="true">
                    <span className="wt-timeline-fill" style={{ width: `${progress * 100}%` }} />
                </div>
            )}
            <dl className="wt-timeline-marks">
                <div className="wt-timeline-mark">
                    <dt>Started</dt>
                    <dd>
                        {startedAt
                            ? `${formatInTimeZone(startedAt, timeZone)} ${getTimeZoneLabel(startedAt, timeZone)}`.trim()
                            : 'Not started'}
                    </dd>
                </div>
                <div className="wt-timeline-mark wt-timeline-mark--end">
                    <dt>Ends</dt>
                    <dd data-testid="wt-cycle-ends">
                        {endsAt ? (
                            <>
                                <span>
                                    {`${formatInTimeZone(endsAt, timeZone)} ${getTimeZoneLabel(endsAt, timeZone)}`.trim()}
                                </span>
                                <Countdown target={endsAt} passedLabel="ended" />
                            </>
                        ) : (
                            'Not set'
                        )}
                    </dd>
                </div>
            </dl>
        </div>
    );
};

const WorkTrial = () => {
    const { workTrialId } = useParams();
    const navigate = useNavigate();
    const chatContainerRef = useRef(null);

    // Add refs for state values used in Firebase listener
    const activeTabRef = useRef('chat');
    const browserVisibleRef = useRef(true);
    const soundEnabledRef = useRef(true);
    const lastMessageCountRef = useRef(0);
    
    const [user, setUser] = useState(null);
    const [workTrial, setWorkTrial] = useState(null);
    // The cycle a cycle-sourced trial belongs to, for its version label and the
    // timezone its timeline is read in. Job trials never load it.
    const [cycleContext, setCycleContext] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [projects, setProjects] = useState([]);
    const [interviews, setInterviews] = useState([]);
    // Separate drafts so a message typed in one chat can never be sent to the other
    const [individualDraft, setIndividualDraft] = useState('');
    const [groupDraft, setGroupDraft] = useState('');
    const [individualSendError, setIndividualSendError] = useState(null);
    const [groupSendError, setGroupSendError] = useState(null);
    const [pendingSends, setPendingSends] = useState([]); // local echoes of in-flight sends
    const [chatConnectionError, setChatConnectionError] = useState('');
    const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);
    const [loading, setLoading] = useState(true);
    const [chatLoading, setChatLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [interviewsLoading, setInterviewsLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [refreshError, setRefreshError] = useState(''); // non-destructive banner for post-load fetch failures
    const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'projects', or 'interviews'
    const [downloadingFiles, setDownloadingFiles] = useState({}); // Track which projects are downloading files

    // Workspace chrome: popover menus, sliding tab indicator, per-tab first-visit tours
    const [openMenu, setOpenMenu] = useState(null); // 'settings' | 'members' | null
    const menuAreaRef = useRef(null);
    const tabRefs = useRef({});
    const tabRailRef = useRef(null);
    const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0, ready: false });
    const [activeTabTour, setActiveTabTour] = useState(null);

    // Toasts replace the browser-native alert() dialogs for action feedback
    const [toasts, setToasts] = useState([]);
    const toastIdRef = useRef(0);
    const pushToast = (kind, text) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, text }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 5500);
    };
    const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

    // Which project's drive email is pending removal (drives the confirm dialog)
    const [confirmRemoveGmailId, setConfirmRemoveGmailId] = useState(null);


    // Notification states
    const [notificationPermission, setNotificationPermission] = useState('default');
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastMessageCount, setLastMessageCount] = useState(0);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [browserVisible, setBrowserVisible] = useState(true);
    const [showNotificationBanner, setShowNotificationBanner] = useState(false);
    
    // Submission modal state
    const [showSubmissionModal, setShowSubmissionModal] = useState(false);
    const [submissionProjectId, setSubmissionProjectId] = useState(null);
    const [submissionText, setSubmissionText] = useState('');
    const [submissionFiles, setSubmissionFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    // View submission modal state
    const [showViewSubmissionModal, setShowViewSubmissionModal] = useState(false);
    const [viewingProject, setViewingProject] = useState(null);
    const [lastMarkedSeen, setLastMarkedSeen] = useState(null);
    
    // GitHub setup modal state
    const [showGithubSetupModal, setShowGithubSetupModal] = useState(false);
    const [githubSetupProjectId, setGithubSetupProjectId] = useState(null);
    const [githubUsername, setGithubUsername] = useState('');
    const [settingUpGithub, setSettingUpGithub] = useState(false);
    
    // Google Drive state
    const [projectDriveInfo, setProjectDriveInfo] = useState({}); // Map of project_id -> drive info
    const [loadingDriveInfo, setLoadingDriveInfo] = useState({}); // Map of project_id -> loading state
    const [creatingDoc, setCreatingDoc] = useState({}); // Map of project_id -> creating doc state
    const [creatingSheet, setCreatingSheet] = useState({}); // Map of project_id -> creating sheet state
    const [driveFiles, setDriveFiles] = useState({}); // Map of project_id -> files array
    const [loadingDriveFiles, setLoadingDriveFiles] = useState({}); // Map of project_id -> loading state
    const [driveFilesExpanded, setDriveFilesExpanded] = useState({}); // Map of project_id -> expanded state
    
    // Gmail setup modal state
    const [showGmailSetupModal, setShowGmailSetupModal] = useState(false);
    const [gmailSetupProjectId, setGmailSetupProjectId] = useState(null);
    const [gmailInput, setGmailInput] = useState('');
    const [settingUpGmail, setSettingUpGmail] = useState(false);
    const [removingGmail, setRemovingGmail] = useState(false);
    
    // Firebase state
    const [firebaseAuthenticated, setFirebaseAuthenticated] = useState(false);
    const [chatUnsubscribe, setChatUnsubscribe] = useState(null);
    const [groupChatUnsubscribe, setGroupChatUnsubscribe] = useState(null);

    // Group state
    const [groupData, setGroupData] = useState(null);
    const [groupMessages, setGroupMessages] = useState([]);
    const [groupProjects, setGroupProjects] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupChatMode, setGroupChatMode] = useState(false); // Toggle between individual and group chat
    // projectMode state removed - using activeTab directly for 'projects' vs 'group-projects'
    // Alias-based group identity — peers only ever see aliases. See WORK_TRIAL_ALIAS_PLAN.md.
    const [groupMembers, setGroupMembers] = useState([]); // [{ alias, is_me }]
    const [myAlias, setMyAlias] = useState('');
    const [myAliasSet, setMyAliasSet] = useState(false);
    const [aliasInput, setAliasInput] = useState('');
    const [aliasError, setAliasError] = useState('');
    const [aliasSubmitting, setAliasSubmitting] = useState(false);
    const [showNameWarning, setShowNameWarning] = useState(false);
    const [pendingMessage, setPendingMessage] = useState('');
    
    // List-detail layout states
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedInterview, setSelectedInterview] = useState(null);
    
    // Auto-refresh states
    const [autoRefreshEnabled] = useState(true);
    const REFRESH_INTERVAL = 3600000; // 3600 seconds (1 hour)
    
    
    // Logging states
    const activeTabForCloseRef = useRef(activeTab);

    // Scroll-follow state: autoscroll only when the reader is already at the bottom
    const isAtBottomRef = useRef(true);
    const prevChatLenRef = useRef(0);
    const prevGroupLenRef = useRef(0);
    // Full-page error is reserved for the initial load; later fetch failures downgrade to a banner
    const initialLoadDoneRef = useRef(false);

    const handleBackToList = () => {
        setSelectedProject(null);
        setSelectedInterview(null);
    };

    // Update refs when state changes
    useEffect(() => {
        activeTabRef.current = activeTab;
        activeTabForCloseRef.current = activeTab;
    }, [activeTab]);

    // Auto-select first item when switching tabs or when data loads (desktop only)
    useEffect(() => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) return;
        if (activeTab === 'projects' || activeTab === 'group-projects') {
            const currentProjects = activeTab === 'group-projects' && groupData ? groupProjects : projects;
            if (currentProjects.length > 0 && !selectedProject) {
                setSelectedProject(currentProjects[0]);
            }
        }
        if (activeTab === 'interviews' && interviews.length > 0 && !selectedInterview) {
            setSelectedInterview(interviews[0]);
        }
    }, [activeTab, projects, groupProjects, groupData, selectedProject, interviews, selectedInterview]);

    // Dismiss any open popover on outside click or Escape
    useEffect(() => {
        if (!openMenu) return;
        const onPointerDown = (event) => {
            if (menuAreaRef.current && !menuAreaRef.current.contains(event.target)) {
                setOpenMenu(null);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpenMenu(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [openMenu]);

    // Sliding indicator tracks the active tab (re-measured when tab set or badge width changes)
    useLayoutEffect(() => {
        const measure = () => {
            const el = tabRefs.current[activeTab];
            const rail = tabRailRef.current;
            if (!el || !rail) return;
            const railRect = rail.getBoundingClientRect();
            const rect = el.getBoundingClientRect();
            setTabIndicator({
                left: rect.left - railRect.left + rail.scrollLeft,
                width: rect.width,
                ready: true,
            });
        };
        measure();
        window.addEventListener('resize', measure);
        let alive = true;
        document.fonts?.ready?.then(() => { if (alive) measure(); });
        return () => {
            alive = false;
            window.removeEventListener('resize', measure);
        };
    // `loading` gates the first render of the tab rail, so re-measure when it flips
    }, [activeTab, groupData, unreadCount, loading]);

    // First-visit tours per tab: shown once, desktop only, and only when the tab
    // actually has content assigned (first project, first interview, ...)
    useEffect(() => {
        if (window.innerWidth <= 768) return;
        const eligibleByTab = {
            projects: !projectsLoading && projects.length > 0,
            'group-chat': !!groupData && !groupLoading,
            'group-projects': !!groupData && !groupLoading && groupProjects.length > 0,
            interviews: !interviewsLoading && interviews.length > 0,
        };
        if (!eligibleByTab[activeTab]) return;
        if (localStorage.getItem(`wt-tour-seen-${activeTab}`)) return;
        const timer = setTimeout(() => {
            // Never stack on the server-driven page tour (PopupManager)
            if (document.querySelector('.guided-tour-overlay')) return;
            setActiveTabTour(activeTab);
        }, 500);
        return () => clearTimeout(timer);
    }, [activeTab, projectsLoading, projects.length, groupData, groupLoading, groupProjects.length, interviewsLoading, interviews.length]);

    const completeTabTour = (tab) => {
        localStorage.setItem(`wt-tour-seen-${tab}`, '1');
        setActiveTabTour(null);
    };

    // Clear or update selected project when switching between individual and group project tabs
    useEffect(() => {
        if (activeTab === 'projects' || activeTab === 'group-projects') {
            const isMobile = window.innerWidth <= 768;
            const currentProjects = activeTab === 'group-projects' && groupData ? groupProjects : projects;
            
            // Only auto-select on desktop
            if (!isMobile) {
                // If there's a selected project, check if it belongs to the current tab
                if (selectedProject) {
                    const projectExistsInCurrentTab = currentProjects.some(
                        p => p.project_id === selectedProject.project_id
                    );
                    
                    // If the selected project doesn't exist in current tab, select first project or clear
                    if (!projectExistsInCurrentTab) {
                        if (currentProjects.length > 0) {
                            setSelectedProject(currentProjects[0]);
                        } else {
                            setSelectedProject(null);
                        }
                    }
                } else {
                    // If no project selected, select first one
                    if (currentProjects.length > 0) {
                        setSelectedProject(currentProjects[0]);
                    }
                }
            }
            // On mobile: don't clear if user manually selected - only clear when switching tabs
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, projects, groupProjects, groupData]); // Removed selectedProject and projectMode from dependencies

    useEffect(() => {
        const isMobile = window.innerWidth <= 768;
        if (activeTab === 'interviews' && !isMobile) {
            if (interviews.length > 0 && !selectedInterview) {
                setSelectedInterview(interviews[0]);
            }
        }
    }, [activeTab, interviews, selectedInterview]);

    // Fetch drive info when a project is selected
    useEffect(() => {
        if (selectedProject && !projectDriveInfo[selectedProject.project_id] && !loadingDriveInfo[selectedProject.project_id]) {
            const isGroupProject = activeTab === 'group-projects' && groupData;
            fetchProjectDriveInfo(selectedProject.project_id, isGroupProject);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject, activeTab, groupData]);

    useEffect(() => {
        browserVisibleRef.current = browserVisible;
    }, [browserVisible]);

    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    useEffect(() => {
        lastMessageCountRef.current = lastMessageCount;
    }, [lastMessageCount]);

    // Auto-clear error messages after 10 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 10000);
            
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Request notification permission
    const requestNotificationPermission = async () => {
        if (!("Notification" in window)) {
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            setNotificationPermission(permission);
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    };

    // Show browser notification
    const showNotification = (message, sender = 'AI Assistant') => {
        if (notificationPermission !== 'granted' || !("Notification" in window)) {
            return;
        }

        // Get current state values to avoid closure issues
        const currentActiveTab = activeTabRef.current;
        const currentBrowserVisible = browserVisibleRef.current;

        // Don't show notification if user is on chat tab and browser is visible
        if (currentActiveTab === 'chat' && currentBrowserVisible) {
            return;
        }

        try {
            const notification = new Notification(`New message from ${sender}`, {
                body: message && message.length > 100 ? message.substring(0, 100) + '...' : message,
                icon: '/favicon.ico', // You can replace with your app icon
                badge: '/favicon.ico',
                tag: 'work-trial-message', // This prevents multiple notifications from stacking
                requireInteraction: false,
                silent: false
            });

            // Auto-close notification after 5 seconds
            setTimeout(() => {
                notification.close();
            }, 5000);

            // Handle notification click - focus the browser window and switch to chat tab
            notification.onclick = () => {
                window.focus();
                setActiveTab('chat');
                notification.close();
            };

        } catch (error) {
            console.error('❌ Error showing notification:', error);
        }
    };

    // Play notification sound
    const playNotificationSound = () => {
        if (!soundEnabledRef.current) {
            return;
        }

        try {
            // Create audio context for notification sound
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
            console.error('❌ Error playing notification sound:', error);

            // Fallback: try using a simple beep
            try {
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const oscillator = audioContext.createOscillator();
                oscillator.connect(audioContext.destination);
                oscillator.frequency.value = 800;
                oscillator.start();
                oscillator.stop(audioContext.currentTime + 0.2);
            } catch (fallbackError) {
                console.error('❌ Fallback sound also failed:', fallbackError);
            }
        }
    };

    // Track browser visibility (browser tab switching, not page tabs)
    useEffect(() => {
        const handleVisibilityChange = () => {
            const isHidden = document.hidden;
            setBrowserVisible(!isHidden);
            
            // Log BROWSER tab visibility changes only (not internal page tab changes)
            // document.hidden changes when switching between browser tabs/windows
            if (isHidden) {
                logActivity('website-activity', 'leave-tab');
            } else {
                logActivity('website-activity', 'open-tab');
            }
            
            // Clear unread count when user comes back to the page
            if (!isHidden && activeTab === 'chat') {
                setTimeout(() => {
                    setUnreadCount(0);
                    markMessagesAsSeen();
                }, 500);
            }
        };

        // This event only fires when the browser tab visibility changes
        // It does NOT fire when switching between Chat/Projects/Interviews tabs
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Update document title with unread count
    useEffect(() => {
        if (unreadCount > 0) {
            document.title = `(${unreadCount}) Work Trial - New Messages`;
        } else {
            document.title = 'Work Trial';
        }

        return () => {
            document.title = 'Work Trial';
        };
    }, [unreadCount]);

    // Check Firebase authentication state (user should already be signed in from login)
    const checkFirebaseAuthentication = () => {
        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (user) {
                    setFirebaseAuthenticated(true);
                    resolve(true);
                } else {
                    setFirebaseAuthenticated(false);
                    resolve(false);
                }
                unsubscribe(); // Unsubscribe after first check
            });
        });
    };

    // Set up real-time chat listener
    const setupChatListener = () => {
        if (!firebaseAuthenticated || !workTrialId) {
            return;
        }

        try {
            const chatsCollection = collection(db, 'work-trials', workTrialId, 'chats');
            const chatsQuery = query(chatsCollection, orderBy('created_at'));

            const unsubscribe = onSnapshot(chatsQuery,
                (snapshot) => {
                    const messages = snapshot.docs.map(doc => {
                        const data = doc.data();
                        return {
                            message_id: doc.id,
                            ...data,
                            // Convert Firestore timestamp to string if needed
                            created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at
                        };
                    });
                    
                    // Check for new messages and trigger notifications
                    const previousCount = lastMessageCountRef.current;
                    const currentCount = messages.length;

                    if (currentCount > previousCount) {
                        // New message(s) arrived
                        const newMessages = messages.slice(previousCount);
                        const newAiMessages = newMessages.filter(msg => msg.sender !== 'user');

                        if (newAiMessages.length > 0) {
                            // Use refs to get current state values to avoid closure issues
                            const currentActiveTab = activeTabRef.current;
                            const currentBrowserVisible = browserVisibleRef.current;
                            const currentSoundEnabled = soundEnabledRef.current;

                            // Update unread count only if not on chat tab or browser not visible
                            if (currentActiveTab !== 'chat' || !currentBrowserVisible) {
                                setUnreadCount(prev => prev + newAiMessages.length);
                            }

                            // Play sound for new messages if enabled
                            if (currentSoundEnabled) {
                                playNotificationSound();
                            }

                            // Show desktop notification
                            const latestAiMessage = newAiMessages[newAiMessages.length - 1];
                            showNotification(latestAiMessage.message, 'AI Assistant');
                        }
                    }

                    setChatMessages(messages);
                    setLastMessageCount(currentCount);
                    setChatLoading(false);
                    setChatConnectionError('');

                    // Mark messages as seen if we're on the chat tab and browser is visible
                    setTimeout(() => {
                        const currentActiveTab = activeTabRef.current;
                        const currentBrowserVisible = browserVisibleRef.current;
                        if (currentActiveTab === 'chat' && currentBrowserVisible && messages.length > 0) {
                            setUnreadCount(0);
                            markMessagesAsSeen();
                        }
                    }, 500);
                },
                (error) => {
                    console.error('❌ Error in chat listener:', error);
                    // Surface inside the chat pane; the page-level error screen is
                    // reserved for initial load failures.
                    if (error.code === 'permission-denied') {
                        setChatConnectionError('Access denied to chat messages');
                    } else {
                        setChatConnectionError('Chat connection lost. Refresh the page to reconnect.');
                    }
                    setChatLoading(false);
                }
            );

            setChatUnsubscribe(() => unsubscribe);

        } catch (error) {
            console.error('❌ Error setting up chat listener:', error);
            setChatConnectionError('Chat connection failed. Refresh the page to reconnect.');
            setChatLoading(false);
        }
    };

    // Set up real-time group chat listener
    const setupGroupChatListener = () => {
        if (!firebaseAuthenticated || !groupData?.group_id) {
            return;
        }

        try {
            const groupChatsCollection = collection(db, 'groups', groupData.group_id, 'chats');
            const groupChatsQuery = query(groupChatsCollection, orderBy('created_at'));

            const unsubscribe = onSnapshot(groupChatsQuery,
                (snapshot) => {
                    const messages = snapshot.docs.map(doc => {
                        const data = doc.data();
                        return {
                            message_id: doc.id,
                            ...data,
                            // Convert Firestore timestamp to string if needed
                            created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at
                        };
                    });

                    setGroupMessages(messages);
                },
                (error) => {
                    console.error('❌ Error in group chat listener:', error);
                    if (error.code === 'permission-denied') {
                        setChatConnectionError('Access denied to group chat messages');
                    } else {
                        setChatConnectionError('Group chat connection lost. Refresh the page to reconnect.');
                    }
                }
            );

            setGroupChatUnsubscribe(() => unsubscribe);

        } catch (error) {
            console.error('❌ Error setting up group chat listener:', error);
            setChatConnectionError('Group chat connection failed. Refresh the page to reconnect.');
        }
    };

    // Function to log work trial access
    const logWorkTrialAccess = async () => {
        try {
            await API.post(`work-trials/${workTrialId}/logs/add/opened`, {});
        } catch (error) {
            console.error("Error logging work trial access:", error);
            // Don't show user error for this background operation as it's not critical
        }
    };

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate("/login");
            return;
        }

        // Request notification permission on component mount
        if ("Notification" in window) {
            const currentPermission = Notification.permission;
            setNotificationPermission(currentPermission);
            
            // Show banner if notifications are not granted and user hasn't dismissed it
            const bannerDismissed = localStorage.getItem('notification-banner-dismissed');
            if (currentPermission !== 'granted' && !bannerDismissed) {
                setTimeout(() => setShowNotificationBanner(true), 3000); // Show after 3 seconds
            }
            
            // When permission is 'default' we don't request automatically — the
            // banner lets the user choose.
        }

        API.get("users/account-data").then(async (response) => {
            setUser(response.data.user);
            
            // Check Firebase authentication (user should already be signed in)
            const firebaseAuth = await checkFirebaseAuthentication();
            
            // Fetch work trial data
            await fetchWorkTrialData();
            
            // Fetch group data (optional - won't error if no group exists)
            await fetchGroupData();

            initialLoadDoneRef.current = true;

            // Log work trial access after successful authentication and data loading
            await logWorkTrialAccess();
            
            // If Firebase auth successful, set up listener, otherwise use fallback
            if (firebaseAuth) {
                // Wait a moment for state to update then set up listener
                setTimeout(() => {
                    if (workTrialId) {
                        setupChatListener();
                    }
                }, 100);
            } else {
                console.warn('⚠️ Firebase not authenticated, using API fallback for chat');
                // Fallback to API calls
                await fetchChatMessages();
            }
            
        }).catch((err) => {
            console.error("Error fetching user data:", err);
            navigate("/login");
        });

        // Cleanup function
        return () => {
            if (chatUnsubscribe) {
                chatUnsubscribe();
            }
            if (groupChatUnsubscribe) {
                groupChatUnsubscribe();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId, navigate]);

    // Set up chat listener when Firebase authentication completes
    useEffect(() => {
        if (firebaseAuthenticated && workTrialId) {
            setupChatListener();
        }
        
        // Cleanup previous listener when dependencies change
        return () => {
            if (chatUnsubscribe) {
                chatUnsubscribe();
                setChatUnsubscribe(null);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebaseAuthenticated, workTrialId]);

    // Set up group chat listener when Firebase authentication and group data are ready
    useEffect(() => {
        if (firebaseAuthenticated && groupData?.group_id) {
            setupGroupChatListener();
        }
        
        // Cleanup previous group chat listener when dependencies change
        return () => {
            if (groupChatUnsubscribe) {
                groupChatUnsubscribe();
                setGroupChatUnsubscribe(null);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebaseAuthenticated, groupData?.group_id]);

    // Auto-refresh projects periodically
    useEffect(() => {
        if (!autoRefreshEnabled || !workTrialId) {
            return;
        }

        const refreshProjects = async () => {
            try {
                await fetchProjects();
            } catch (error) {
                console.error('Error auto-refreshing projects:', error);
            }
        };

        // Set up interval for auto-refresh (every 1 hour)
        const intervalId = setInterval(refreshProjects, REFRESH_INTERVAL);

        // Cleanup on unmount
        return () => {
            clearInterval(intervalId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId, autoRefreshEnabled]);

    // Auto-refresh interviews periodically
    useEffect(() => {
        if (!autoRefreshEnabled || !workTrialId) {
            return;
        }

        const refreshInterviews = async () => {
            try {
                await fetchInterviews();
            } catch (error) {
                console.error('Error auto-refreshing interviews:', error);
            }
        };

        // Set up interval for auto-refresh (every 1 hour)
        const intervalId = setInterval(refreshInterviews, REFRESH_INTERVAL);

        // Cleanup on unmount
        return () => {
            clearInterval(intervalId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId, autoRefreshEnabled]);

    // Auto-refresh group data periodically (if user is in a group)
    useEffect(() => {
        if (!autoRefreshEnabled || !workTrialId || !groupData) {
            return;
        }

        const refreshGroupData = async () => {
            try {
                await fetchGroupData();
            } catch (error) {
                console.error('Error auto-refreshing group data:', error);
            }
        };

        // Set up interval for auto-refresh (every 1 hour)
        const intervalId = setInterval(refreshGroupData, REFRESH_INTERVAL);

        // Cleanup on unmount
        return () => {
            clearInterval(intervalId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId, autoRefreshEnabled, groupData?.group_id]);

    // Periodic "still-on-website" heartbeat logging
    useEffect(() => {
        if (!workTrialId) {
            return;
        }

        // Log initial page load ONLY once when component first mounts
        // Use a ref to track if we've already logged the initial load
        const hasLoggedInitialLoad = sessionStorage.getItem(`work-trial-loaded-${SESSION_ID}`);
        
        if (!hasLoggedInitialLoad) {
            logActivity('website-activity', 'open-tab', {
                initial_load: true
            });
            sessionStorage.setItem(`work-trial-loaded-${SESSION_ID}`, 'true');
        }

        // Set up periodic heartbeat every 5 minutes (300000 ms)
        const HEARTBEAT_INTERVAL = 300000; // 5 minutes
        
        const heartbeatInterval = setInterval(() => {
            // Only log if user is still on the page (not hidden)
            if (!document.hidden) {
                logActivity('website-activity', 'still-on-website', {
                    duration_minutes: 5,
                    current_tab: activeTab
                });
            }
        }, HEARTBEAT_INTERVAL);

        // Cleanup on unmount
        return () => {
            clearInterval(heartbeatInterval);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId]);

    // Log page unload/close
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Log synchronously using a best-effort approach
            // Note: Modern browsers limit what can be done in beforeunload
            // We'll rely on the component unmount log for navigation events
            try {
                // Mark that the page is being closed in sessionStorage
                sessionStorage.setItem(`work-trial-closing-${SESSION_ID}`, 'true');
            } catch (error) {
                console.error('Error marking page close:', error);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            
            // Only log close event if the component is actually unmounting
            // (navigation away from WorkTrial page or browser close)
            // NOT when internal state changes
            const isClosing = sessionStorage.getItem(`work-trial-closing-${SESSION_ID}`) === 'true';
            
            // Log close event when component unmounts (navigation away from WorkTrial page)
            // Use ref to get current activeTab value without causing effect to re-run
            logActivity('website-activity', 'close', {
                final_tab: activeTabForCloseRef.current,
                reason: isClosing ? 'page-close' : 'navigation'
            });
            
            // Clean up session storage
            sessionStorage.removeItem(`work-trial-closing-${SESSION_ID}`);
            sessionStorage.removeItem(`work-trial-loaded-${SESSION_ID}`);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workTrialId]); // Only depend on workTrialId, not activeTab - this ensures cleanup only runs on actual unmount

    // Function to mark messages as seen
    const markMessagesAsSeen = async () => {
        // Throttle to prevent too many API calls - only call once per 30 seconds
        const now = Date.now();
        if (lastMarkedSeen && (now - lastMarkedSeen) < 30000) {
            return;
        }
        
        try {
            const response = await API.post(`work-trials/${workTrialId}/messages/mark_seen`, {});

            if (response.data) {
                setLastMarkedSeen(now);
            }
            
        } catch (error) {
            console.error("Error marking messages as seen:", error);
            // Don't show user error for this background operation
        }
    };

    // A cycle trial is named by the structure version it runs and read in the
    // cycle's timezone, neither of which is on the trial document. The enrolment
    // list is the source that keeps answering once the cycle has ended, and it is
    // only ever the candidate's own — so a failure here just costs the version
    // label and the zone, never the page.
    const fetchCycleContext = async (cycleId) => {
        try {
            const rows = await getMyEnrolments();
            setCycleContext(rows.find((row) => row.cycle?.cycle_id === cycleId)?.cycle || null);
        } catch (error) {
            console.error('Error fetching cycle context:', error);
        }
    };

    const fetchWorkTrialData = async () => {
        try {
            setLoading(true);

            // Fetch work trial details
            const workTrialResponse = await API.get(`work-trials/${workTrialId}`);

            if (workTrialResponse.data && workTrialResponse.data.work_trial) {
                const trial = workTrialResponse.data.work_trial;
                setWorkTrial(trial);

                if (isCycleTrial(trial) && trial.cycle_id) {
                    fetchCycleContext(trial.cycle_id);
                }
            }

            // Fetch projects and interviews in parallel
            await Promise.all([
                fetchProjects(),
                fetchInterviews()
            ]);

        } catch (error) {
            console.error("Error fetching work trial data:", error);
            if (error.response?.status === 404) {
                setError("Work trial not found");
            } else if (error.response?.status === 403) {
                setError("Access denied");
            } else {
                setError("Failed to load work trial data");
            }
        } finally {
            setLoading(false);
        }
    };

    // Fallback function for when Firebase is not available
    const fetchChatMessages = async () => {
        try {
            setChatLoading(true);
            
            const chatResponse = await API.get(`work-trials/${workTrialId}/conversation`);
            
            if (chatResponse.data && chatResponse.data.chat_messages) {
                setChatMessages(chatResponse.data.chat_messages);
                setChatConnectionError('');

                // Mark messages as seen if we're currently on the chat tab
                if (activeTab === 'chat' && chatResponse.data.chat_messages.length > 0) {
                    // Add a small delay to ensure state is updated
                    setTimeout(() => {
                        if (workTrialId) {
                            markMessagesAsSeen();
                        }
                    }, 500);
                }
            }

        } catch (error) {
            console.error("Error fetching chat messages:", error);
            // Don't show error for empty conversations
            if (error.response?.status !== 404) {
                setChatConnectionError('Failed to load the conversation. Refresh the page to retry.');
            }
        } finally {
            setChatLoading(false);
        }
    };

    const fetchProjects = async () => {
        try {
            setProjectsLoading(true);
            
            const projectsResponse = await API.get(`work-trials/${workTrialId}/projects`);
            
            if (projectsResponse.data && projectsResponse.data.projects) {
                setProjects(projectsResponse.data.projects);
            }
            
        } catch (error) {
            console.error("Error fetching projects:", error);
            // Don't show error for empty projects
            if (error.response?.status !== 404) {
                if (initialLoadDoneRef.current) {
                    setRefreshError('Failed to refresh projects. Shown data may be out of date.');
                } else {
                    setError("Failed to load projects");
                }
            }
        } finally {
            setProjectsLoading(false);
        }
    };

    const fetchInterviews = async () => {
        try {
            setInterviewsLoading(true);
            
            const interviewsResponse = await API.get(`interviews/${workTrialId}/get`);
            
            if (interviewsResponse.data && interviewsResponse.data.interviews) {
                setInterviews(interviewsResponse.data.interviews);
            }
            
        } catch (error) {
            console.error("Error fetching interviews:", error);
            // Don't show error for empty interviews
            if (error.response?.status !== 404) {
                if (initialLoadDoneRef.current) {
                    setRefreshError('Failed to refresh interviews. Shown data may be out of date.');
                } else {
                    setError("Failed to load interviews");
                }
            }
        } finally {
            setInterviewsLoading(false);
        }
    };

    // Group API functions
    const fetchGroupData = async () => {
        try {
            setGroupLoading(true);
            
            const groupResponse = await API.get(`work-trials/${workTrialId}/group/get`);
            
            if (groupResponse.data) {
                setGroupData(groupResponse.data.group);
                setGroupProjects(groupResponse.data.projects || []);
                setGroupMembers(groupResponse.data.members || []);
                setMyAlias(groupResponse.data.my_alias || '');
                setMyAliasSet(!!groupResponse.data.my_alias_set);
                
                // Set up Firebase listener for group chat if Firebase is authenticated
                if (firebaseAuthenticated && groupResponse.data.group?.group_id) {
                    setTimeout(() => {
                        setupGroupChatListener();
                    }, 100);
                } else {
                    // Fallback: set group messages from API response
                    setGroupMessages(groupResponse.data.messages || []);
                }
            }
            
        } catch (error) {
            console.error("Error fetching group data:", error);
            // Don't show error if no group exists - this is optional
            if (error.response?.status !== 404) {
                if (initialLoadDoneRef.current) {
                    setRefreshError('Failed to refresh group data. Shown data may be out of date.');
                } else {
                    setError("Failed to load group data");
                }
            }
        } finally {
            setGroupLoading(false);
        }
    };

    const sendGroupMessage = async (messageText) => {
        if (!groupData?.group_id) {
            throw new Error('No group data');
        }
        // Group messages are written server-side so the backend stamps the sender's
        // alias (never the real user_id). The Firestore listener renders it in real time.
        await API.post(`work-trials/${workTrialId}/group/send-message`, {
            message: messageText
        });
    };

    // Submit the chosen group alias (one-time, immutable). Surfaces backend error codes.
    const submitAlias = async () => {
        const alias = aliasInput.trim();
        if (!alias) {
            setAliasError('Please enter a name.');
            return;
        }
        setAliasSubmitting(true);
        setAliasError('');
        try {
            const res = await API.post(`work-trials/${workTrialId}/group/set-alias`, { alias });
            setMyAlias(res.data.alias || alias);
            setMyAliasSet(true);
            setAliasInput('');
            await fetchGroupData();
        } catch (error) {
            const code = error.response?.data?.error;
            const messages = {
                alias_taken: 'That name is already taken in your group. Please choose another.',
                alias_reserved: "That name isn't allowed. Please choose another.",
                alias_empty: 'Please enter a name.',
                alias_too_short: 'That name is too short.',
                alias_too_long: 'That name is too long (max 24 characters).',
                alias_already_set: 'Your alias is already set and cannot be changed.',
                // A cohort trial has no alias to set here: its members are named by the
                // workspace display name they choose in their profile, and the route says
                // so rather than answering the missing posting's 404. This page cannot
                // open that form, so it says where the name lives.
                alias_from_profile:
                    'Your name here comes from your workspace profile. Set it there.',
            };
            setAliasError(messages[code] || 'Could not set your name. Please try again.');
            if (code === 'alias_already_set') {
                setMyAliasSet(true);
            }
        } finally {
            setAliasSubmitting(false);
        }
    };

    // Warn if a candidate is about to type their own real name into the group chat.
    // Matches the first OR last name (case-insensitive, whole-word).
    const mentionsOwnRealName = (text) => {
        if (!text || !user) return false;
        const first = (user.first_name || '').trim().toLowerCase();
        const last = (user.last_name || '').trim().toLowerCase();
        const names = [];
        if (first && first.length >= 2) names.push(first);
        if (last && last.length >= 2) names.push(last);
        return names.some((name) => {
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return new RegExp(`(^|[^a-z])${escaped}([^a-z]|$)`, 'i').test(text);
        });
    };

    const submitGroupProject = async (projectId, submissionText, submissionFiles = []) => {
        try {
            // Use FormData for file uploads similar to individual projects
            const formData = new FormData();
            formData.append('project_id', projectId);
            formData.append('submission', submissionText);
            
            // Add files to form data
            submissionFiles.forEach((file, index) => {
                formData.append('files', file);
            });
            
            const response = await API.post(`work-trials/${workTrialId}/group/submit-project`, formData);

            if (response.data) {
                // Refresh group data to get latest projects
                await fetchGroupData();
                return response.data;
            }

        } catch (error) {
            console.error("Error submitting group project:", error);
            throw error; // Re-throw to handle in calling function
        }
    };

    // Google Drive Functions
    const fetchProjectDriveInfo = async (projectId, isGroupProject = false) => {
        try {
            setLoadingDriveInfo(prev => ({ ...prev, [projectId]: true }));
            
            const endpoint = isGroupProject 
                ? `work-trials/${workTrialId}/group/project/${projectId}/drive-info`
                : `work-trials/${workTrialId}/project/${projectId}/drive-info`;
            
            const response = await API.get(endpoint);
            
            if (response.data) {
                setProjectDriveInfo(prev => ({ ...prev, [projectId]: response.data }));
            }
        } catch (error) {
            console.error("Error fetching drive info:", error);
            setProjectDriveInfo(prev => ({ ...prev, [projectId]: { has_drive: false } }));
        } finally {
            setLoadingDriveInfo(prev => ({ ...prev, [projectId]: false }));
        }
    };

    const handleCreateDoc = async (projectId, isGroupProject = false) => {
        try {
            setCreatingDoc(prev => ({ ...prev, [projectId]: true }));
            
            const endpoint = isGroupProject
                ? `work-trials/${workTrialId}/group/project/${projectId}/create-doc`
                : `work-trials/${workTrialId}/project/${projectId}/create-doc`;
            
            const response = await API.post(endpoint, {
                name: `Document - ${new Date().toLocaleString()}`
            });
            
            if (response.data && response.data.success) {
                pushToast('success', 'Document created — opening it in a new tab.');
                // Refresh file list
                fetchDriveFiles(projectId, isGroupProject);
                // Open the document directly if available, otherwise open the drive folder
                if (response.data.doc_link) {
                    window.open(response.data.doc_link, '_blank');
                } else {
                    const driveInfo = projectDriveInfo[projectId];
                    if (driveInfo && driveInfo.drive_link) {
                        window.open(driveInfo.drive_link, '_blank');
                    }
                }
            }
        } catch (error) {
            console.error("Error creating doc:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't create the document. Please try again.");
            }
        } finally {
            setCreatingDoc(prev => ({ ...prev, [projectId]: false }));
        }
    };

    const handleCreateSheet = async (projectId, isGroupProject = false) => {
        try {
            setCreatingSheet(prev => ({ ...prev, [projectId]: true }));
            
            const endpoint = isGroupProject
                ? `work-trials/${workTrialId}/group/project/${projectId}/create-sheet`
                : `work-trials/${workTrialId}/project/${projectId}/create-sheet`;
            
            const response = await API.post(endpoint, {
                name: `Spreadsheet - ${new Date().toLocaleString()}`
            });
            
            if (response.data && response.data.success) {
                pushToast('success', 'Spreadsheet created — opening it in a new tab.');
                // Refresh file list
                fetchDriveFiles(projectId, isGroupProject);
                // Open the sheet directly if available, otherwise open the drive folder
                if (response.data.sheet_link) {
                    window.open(response.data.sheet_link, '_blank');
                } else {
                    const driveInfo = projectDriveInfo[projectId];
                    if (driveInfo && driveInfo.drive_link) {
                        window.open(driveInfo.drive_link, '_blank');
                    }
                }
            }
        } catch (error) {
            console.error("Error creating sheet:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't create the spreadsheet. Please try again.");
            }
        } finally {
            setCreatingSheet(prev => ({ ...prev, [projectId]: false }));
        }
    };

    // Fetch drive files for file tree display
    const fetchDriveFiles = async (projectId, isGroupProject = false) => {
        try {
            setLoadingDriveFiles(prev => ({ ...prev, [projectId]: true }));
            
            const endpoint = isGroupProject 
                ? `work-trials/${workTrialId}/group/project/${projectId}/drive-files`
                : `work-trials/${workTrialId}/project/${projectId}/drive-files`;
            
            const response = await API.get(endpoint);
            
            if (response.data && response.data.files) {
                setDriveFiles(prev => ({ ...prev, [projectId]: response.data.files }));
            }
        } catch (error) {
            console.error("Error fetching drive files:", error);
            setDriveFiles(prev => ({ ...prev, [projectId]: [] }));
        } finally {
            setLoadingDriveFiles(prev => ({ ...prev, [projectId]: false }));
        }
    };

    // Toggle file tree expansion
    const toggleDriveFilesExpanded = (projectId, isGroupProject = false) => {
        const isExpanded = driveFilesExpanded[projectId];
        setDriveFilesExpanded(prev => ({ ...prev, [projectId]: !isExpanded }));
        
        // Fetch files if expanding and not already loaded
        if (!isExpanded && !driveFiles[projectId]) {
            fetchDriveFiles(projectId, isGroupProject);
        }
    };

    // Gmail setup functions
    const handleOpenGmailSetupModal = (projectId) => {
        setGmailSetupProjectId(projectId);
        setGmailInput('');
        setShowGmailSetupModal(true);
    };

    const handleCloseGmailSetupModal = () => {
        setShowGmailSetupModal(false);
        setGmailSetupProjectId(null);
        setGmailInput('');
    };

    const handleSetupGmail = async () => {
        if (!gmailInput.trim()) {
            pushToast('error', 'Enter an email address first.');
            return;
        }

        const isGroupProject = activeTab === 'group-projects' && groupData;

        try {
            setSettingUpGmail(true);
            
            const endpoint = isGroupProject
                ? `work-trials/${workTrialId}/group/project/${gmailSetupProjectId}/setup-gmail`
                : `work-trials/${workTrialId}/project/${gmailSetupProjectId}/setup-gmail`;
            
            const response = await API.post(endpoint, {
                gmail: gmailInput.trim()
            });
            
            if (response.data && response.data.success) {
                pushToast('success', 'Email added — you now have access to the drive.');
                handleCloseGmailSetupModal();
                
                // Refresh project data
                if (isGroupProject) {
                    fetchGroupData();
                } else {
                    fetchProjects();
                }
                
                // Refresh drive info
                fetchProjectDriveInfo(gmailSetupProjectId, isGroupProject);
            }
        } catch (error) {
            console.error("Error setting up Gmail:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't add your email to the drive. Please try again.");
            }
        } finally {
            setSettingUpGmail(false);
        }
    };

    // Called from the confirm dialog — the confirmation itself lives in the UI
    const handleRemoveGmail = async (projectId) => {
        const isGroupProject = activeTab === 'group-projects' && groupData;
        setConfirmRemoveGmailId(null);

        try {
            setRemovingGmail(true);
            
            const endpoint = isGroupProject
                ? `work-trials/${workTrialId}/group/project/${projectId}/remove-gmail`
                : `work-trials/${workTrialId}/project/${projectId}/remove-gmail`;
            
            const response = await API.post(endpoint, {});
            
            if (response.data && response.data.success) {
                pushToast('success', 'Email removed from the drive.');
                
                // Refresh project data
                if (isGroupProject) {
                    fetchGroupData();
                } else {
                    fetchProjects();
                }
                
                // Refresh drive info
                fetchProjectDriveInfo(projectId, isGroupProject);
            }
        } catch (error) {
            console.error("Error removing Gmail:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't remove your email. Please try again.");
            }
        } finally {
            setRemovingGmail(false);
        }
    };

    const handleGetProjectFiles = async (projectId) => {
        try {
            // Set loading state for this specific project
            setDownloadingFiles(prev => ({
                ...prev,
                [projectId]: true
            }));
            
            const response = await API.get(`work-trials/${workTrialId}/project/${projectId}/file_urls`);
            
            if (response.data && response.data.file_urls) {
                const fileUrls = response.data.file_urls;
                
                if (fileUrls.length === 0) {
                    pushToast('info', 'This project has no files yet.');
                    return;
                }
                
                // Log file download activity
                logActivity('project-activity', 'download-files', {
                    project_id: projectId,
                    file_count: fileUrls.length,
                    file_names: fileUrls.map(f => f.original_name || f.file_name)
                });
                
                // Download each file
                for (const fileInfo of fileUrls) {
                    try {
                        // Create a temporary link element and trigger download
                        const link = document.createElement('a');
                        link.href = fileInfo.download_url;
                        link.download = fileInfo.original_name || fileInfo.file_name;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        
                        // Append to body, click, and remove
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        // Small delay between downloads to avoid overwhelming the browser
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                    } catch (downloadError) {
                        console.error(`Error downloading file ${fileInfo.original_name}:`, downloadError);
                    }
                }
                
                pushToast('success', `Downloading ${fileUrls.length} file${fileUrls.length !== 1 ? 's' : ''}…`);
                
            } else {
                pushToast('error', 'No file links came back from the server. Please try again.');
            }
            
        } catch (error) {
            console.error("Error getting project files:", error);
            if (error.response?.status === 404) {
                pushToast('error', 'Project files not found.');
            } else if (error.response?.status === 403) {
                pushToast('error', "You don't have access to these files.");
            } else {
                pushToast('error', "Couldn't get the project files. Please try again.");
            }
        } finally {
            // Clear loading state for this project
            setDownloadingFiles(prev => ({
                ...prev,
                [projectId]: false
            }));
        }
    };

    // Performs the actual send (group or individual). The real-name check lives in
    // handleSendMessage so it can route through the confirmation modal first.
    // Failures stay inline at the composer: the draft is restored and the local
    // echo removed — the page-level error screen is never used for sends.
    const performSend = async (messageText) => {
        const isGroup = groupChatMode && groupData;
        const mode = isGroup ? 'group' : 'individual';
        const pendingId = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        // Clear the draft only when it's what we're sending — a retry of a failed
        // message must not wipe text the user has typed since.
        if (isGroup) {
            setGroupDraft(prev => (prev.trim() === messageText ? '' : prev));
            setGroupSendError(null);
        } else {
            setIndividualDraft(prev => (prev.trim() === messageText ? '' : prev));
            setIndividualSendError(null);
        }

        // Honest local echo: the message renders dimmed as "Sending…" until the
        // Firestore listener delivers the authoritative copy. `baseline` marks the
        // message count at send time so an older identical message can't clear it.
        const baseline = isGroup ? groupMessages.length : chatMessages.length;
        setPendingSends(prev => [...prev, { id: pendingId, mode, text: messageText, baseline }]);
        setSending(true);
        isAtBottomRef.current = true;
        setShowNewMessagesPill(false);
        setTimeout(() => scrollToBottom(true), 50);

        try {
            if (isGroup) {
                // Send group message — the server stamps your alias as the sender
                await sendGroupMessage(messageText);
            } else {
                await API.post(`work-trials/${workTrialId}/message/send`, {
                    message: messageText
                });
            }

            // Without a Firestore listener the echo would never be replaced —
            // refetch so the server copy lands, then drop the echo.
            if (!firebaseAuthenticated) {
                if (isGroup) {
                    await fetchGroupData();
                } else {
                    await fetchChatMessages();
                }
                setPendingSends(prev => prev.filter(p => p.id !== pendingId));
            }

        } catch (error) {
            console.error("Error sending message:", error);
            setPendingSends(prev => prev.filter(p => p.id !== pendingId));

            let failureMessage;
            if (error.response?.data?.error) {
                failureMessage = error.response.data.error;
            } else if (error.response?.status === 403) {
                failureMessage = "Message not sent: access denied.";
            } else if (error.response?.status === 404) {
                failureMessage = "Message not sent: work trial not found.";
            } else if (error.response?.status >= 500) {
                failureMessage = "Message not sent: server error.";
            } else {
                failureMessage = "Message not sent. Check your connection.";
            }

            // Restore the failed text only into an empty composer; if the user typed
            // something new meanwhile, keep it — the banner's retry resends the failed text.
            if (isGroup) {
                setGroupDraft(prev => (prev.trim() === '' ? messageText : prev));
                setGroupSendError({ message: failureMessage, text: messageText });
            } else {
                setIndividualDraft(prev => (prev.trim() === '' ? messageText : prev));
                setIndividualSendError({ message: failureMessage, text: messageText });
            }
        } finally {
            setSending(false);
        }
    };

    const handleSendMessage = async () => {
        const isGroup = groupChatMode && groupData;
        const draft = isGroup ? groupDraft : individualDraft;

        if (!draft.trim() || sending) {
            return;
        }

        const messageText = draft.trim();

        // Group-chat pre-send checks (the composer is also disabled when no alias).
        if (isGroup) {
            if (!myAliasSet) {
                return;
            }
            // If the draft looks like the sender's own real name, confirm via a modal.
            // We intentionally leave the draft in the input until they decide.
            if (mentionsOwnRealName(messageText)) {
                setPendingMessage(messageText);
                setShowNameWarning(true);
                return;
            }
        }

        await performSend(messageText);
    };

    // Resend the failed message itself (not the current draft, which may hold new text)
    const handleRetrySend = async () => {
        const failed = (groupChatMode && groupData) ? groupSendError : individualSendError;
        if (!failed?.text || sending) {
            return;
        }
        await performSend(failed.text);
    };

    // Drop local echoes once the listener delivers the authoritative copy
    useEffect(() => {
        setPendingSends(prev => {
            if (prev.length === 0) return prev;
            return prev.filter(p => {
                if (p.mode === 'individual') {
                    return !chatMessages.slice(p.baseline).some(m =>
                        (m.sender === 'user' || m.sender === user?.user_id) && m.message === p.text);
                }
                return !groupMessages.slice(p.baseline).some(m =>
                    m.sender === myAlias && m.message === p.text);
            });
        });
    }, [chatMessages, groupMessages, myAlias, user]);

    // "Send anyway" from the real-name warning modal
    const confirmSendAnyway = async () => {
        const messageText = pendingMessage;
        setShowNameWarning(false);
        setPendingMessage('');
        if (messageText) {
            await performSend(messageText);
        }
    };

    // "Don't send" — dismiss the warning and leave the draft in the box to edit
    const cancelNameWarning = () => {
        setShowNameWarning(false);
        setPendingMessage('');
    };

    // Scroll helpers. Autoscroll happens ONLY when the reader is already at the
    // bottom (~100px threshold); otherwise new arrivals surface the pill instead
    // of yanking the viewport.
    const scrollToBottom = (smooth = false) => {
        const container = chatContainerRef.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        }
    };

    const handleChatScroll = () => {
        const container = chatContainerRef.current;
        if (!container) return;
        const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        isAtBottomRef.current = atBottom;
        if (atBottom) {
            setShowNewMessagesPill(false);
        }
    };

    // Jump straight to the latest message when a chat tab is opened
    useEffect(() => {
        if (activeTab === 'chat' || activeTab === 'group-chat') {
            isAtBottomRef.current = true;
            setShowNewMessagesPill(false);
            const timer = setTimeout(() => scrollToBottom(), 60);
            return () => clearTimeout(timer);
        }
    }, [activeTab]);

    // Follow new messages when at the bottom; otherwise show the pill
    useEffect(() => {
        if (activeTab !== 'chat' && activeTab !== 'group-chat') return;

        const isGroupTab = activeTab === 'group-chat';
        const messages = isGroupTab ? groupMessages : chatMessages;
        const prevLenRef = isGroupTab ? prevGroupLenRef : prevChatLenRef;
        const previousLength = prevLenRef.current;
        prevLenRef.current = messages.length;

        if (messages.length === 0) return;

        if (isAtBottomRef.current) {
            scrollToBottom();
        } else if (messages.length > previousLength) {
            setShowNewMessagesPill(true);
        }
    }, [chatMessages, groupMessages, activeTab]);

    const jumpToLatest = () => {
        isAtBottomRef.current = true;
        setShowNewMessagesPill(false);
        scrollToBottom(true);
    };

    // Logging utility function
    const logActivity = async (category, action, additionalMetadata = {}) => {
        if (!workTrialId || !isAuthenticated()) {
            return;
        }
        
        try {
            // Build metadata object with session ID and additional data
            const metadata = {
                session_id: SESSION_ID,
                timestamp: new Date().toISOString(),
                user_agent: navigator.userAgent,
                screen_width: window.screen.width,
                screen_height: window.screen.height,
                window_width: window.innerWidth,
                window_height: window.innerHeight,
                ...additionalMetadata
            };
            
            await API.post(`work-trials/${workTrialId}/logs/add`, {
                category: category,
                action: action,
                metadata: metadata
            });
        } catch (error) {
            // Log errors silently without disrupting user experience
            console.error(`Error logging activity ${category}/${action}:`, error);
        }
    };

    // Automatically open projects when user switches to individual projects tab
    useEffect(() => {
        if (activeTab === 'projects' && projects.length > 0) {
            // Find projects that don't have opened_at
            const unopenedProjects = projects.filter(project => !project.opened_at);
            
            if (unopenedProjects.length > 0) {
                // Call set_view for each unopened project
                unopenedProjects.forEach(project => {
                    handleSetProjectView(project.project_id);
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, projects]);

    // Mark messages as seen when switching to chat tab or when messages change
    useEffect(() => {
        if (activeTab === 'chat' && chatMessages.length > 0 && !chatLoading && browserVisible) {
            // Clear unread count immediately when switching to chat tab
            setUnreadCount(0);
            
            // Add a small delay to ensure messages are fully loaded and visible
            const timer = setTimeout(() => {
                markMessagesAsSeen();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, chatMessages.length, chatLoading, browserVisible, workTrialId]);

    // Mark messages as seen when user scrolls in chat (indicating they're actively reading)
    useEffect(() => {
        if (activeTab === 'chat' && chatContainerRef.current) {
            const chatContainer = chatContainerRef.current;
            let scrollTimeout;
            
            const handleScroll = () => {
                // Clear previous timeout
                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                }
                
                // Set new timeout to mark messages as seen after user stops scrolling
                scrollTimeout = setTimeout(() => {
                    const { scrollTop, scrollHeight, clientHeight } = chatContainer;
                    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;
                    
                    if (isNearBottom && chatMessages.length > 0) {
                        markMessagesAsSeen();
                    }
                }, 1000); // Wait 1 second after user stops scrolling
            };
            
            chatContainer.addEventListener('scroll', handleScroll);
            
            return () => {
                chatContainer.removeEventListener('scroll', handleScroll);
                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                }
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, chatMessages.length, workTrialId]);

    const handleSetProjectView = async (projectId) => {
        try {
            const response = await API.get(`work-trials/${workTrialId}/project/${projectId}/set_view`);

            if (response.data) {
                // Update the project in the local state to reflect the opened_at timestamp
                setProjects(prev => prev.map(project =>
                    project.project_id === projectId
                        ? { ...project, opened_at: response.data.opened_at }
                        : project
                ));
            }

        } catch (error) {
            console.error(`Error auto-opening project ${projectId}:`, error);
            // Don't show user alerts for automatic actions, but log the error
            if (error.response) {
                console.error('Response error:', error.response.data);
            }
        }
    };

    const handleOpenSubmissionModal = (projectId) => {
        setSubmissionProjectId(projectId);
        setSubmissionText('');
        setSubmissionFiles([]);
        setShowSubmissionModal(true);
    };

    const handleCloseSubmissionModal = () => {
        if (submitting) return; // Prevent closing while submitting
        
        setShowSubmissionModal(false);
        setSubmissionProjectId(null);
        setSubmissionText('');
        setSubmissionFiles([]);
        setDragOver(false);
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        addFiles(files);
        // Reset the input so the same file can be selected again if needed
        event.target.value = '';
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
        
        const files = Array.from(event.dataTransfer.files);
        addFiles(files);
    };

    const addFiles = (newFiles) => {
        // Find project in the appropriate array based on current tab
        const isGroupMode = activeTab === 'group-projects' && groupData;
        const currentProjects = isGroupMode ? groupProjects : projects;
        const project = currentProjects.find(p => p.project_id === submissionProjectId);
        
        if (!project) return;
        
        const maxFiles = project.max_files_needed || 0;
        const remainingSlots = maxFiles - submissionFiles.length;
        
        if (remainingSlots <= 0) {
            pushToast('info', `This project allows up to ${maxFiles} file${maxFiles !== 1 ? 's' : ''}.`);
            return;
        }
        
        const filesToAdd = newFiles.slice(0, remainingSlots);
        
        if (filesToAdd.length < newFiles.length) {
            pushToast('info', `Only ${filesToAdd.length} added — this project allows up to ${maxFiles} file${maxFiles !== 1 ? 's' : ''}.`);
        }
        
        setSubmissionFiles(prev => [...prev, ...filesToAdd]);
    };

    const removeFile = (indexToRemove) => {
        setSubmissionFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    // Helper function to calculate total file size
    const calculateTotalFileSize = (files) => {
        return files.reduce((total, file) => total + file.size, 0);
    };

    // Large file threshold: 10MB in bytes
    const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024;

    // Function to upload files directly to GCS using signed URLs
    const uploadFilesDirectlyToGCS = async (uploadUrls) => {
        const results = [];
        
        for (let i = 0; i < uploadUrls.length; i++) {
            const urlInfo = uploadUrls[i];
            const file = submissionFiles.find(f => f.name === urlInfo.original_name);
            
            if (!file) {
                throw new Error(`File not found: ${urlInfo.original_name}`);
            }

            try {
                // Upload file directly to GCS using the signed URL
                const uploadResponse = await fetch(urlInfo.upload_url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': urlInfo.content_type
                    },
                    body: file
                });
                
                if (!uploadResponse.ok) {
                    throw new Error(`Upload failed for ${file.name}: ${uploadResponse.status}`);
                }

                results.push({
                    name: file.name,
                    blob_name: urlInfo.blob_name,
                    success: true
                });
            } catch (error) {
                console.error(`❌ Failed to upload ${file.name}:`, error);
                results.push({
                    name: file.name,
                    blob_name: urlInfo.blob_name,
                    success: false,
                    error: error.message
                });
            }
        }
        
        return results;
    };

    // Submit project using direct GCS upload (for large files)
    const submitProjectWithSignedUrls = async (isGroupSubmission) => {
        // Prepare file info for the API
        const filesInfo = submissionFiles.map(file => ({
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream'
        }));
        
        // Step 1: Get signed upload URLs
        let uploadUrlsEndpoint;
        if (isGroupSubmission) {
            uploadUrlsEndpoint = `work-trials/${workTrialId}/group/project/${submissionProjectId}/get-upload-urls`;
        } else {
            uploadUrlsEndpoint = `work-trials/${workTrialId}/project/${submissionProjectId}/get-upload-urls`;
        }
        
        const urlsResponse = await API.post(
            uploadUrlsEndpoint,
            { files: filesInfo }
        );
        
        if (!urlsResponse.data || !urlsResponse.data.upload_urls) {
            throw new Error('Failed to get upload URLs');
        }
        
        const uploadUrls = urlsResponse.data.upload_urls;

        // Step 2: Upload files directly to GCS
        const uploadResults = await uploadFilesDirectlyToGCS(uploadUrls);
        
        // Check if all uploads succeeded
        const failedUploads = uploadResults.filter(r => !r.success);
        if (failedUploads.length > 0) {
            throw new Error(`Failed to upload ${failedUploads.length} file(s): ${failedUploads.map(f => f.name).join(', ')}`);
        }
        
        // Step 3: Confirm submission with expected file sizes for validation
        let confirmEndpoint;
        if (isGroupSubmission) {
            confirmEndpoint = `work-trials/${workTrialId}/group/project/${submissionProjectId}/confirm-submission`;
        } else {
            confirmEndpoint = `work-trials/${workTrialId}/project/${submissionProjectId}/confirm-submission`;
        }
        
        // Build expected_files array with blob names and sizes for server-side validation
        const expectedFiles = uploadUrls.map(urlInfo => {
            const file = submissionFiles.find(f => f.name === urlInfo.original_name);
            return {
                blob_name: urlInfo.blob_name,
                size: file ? file.size : 0
            };
        });
        
        const confirmResponse = await API.post(
            confirmEndpoint,
            { 
                submission: submissionText,
                expected_files: expectedFiles
            }
        );
        
        return confirmResponse.data;
    };

    const handleSubmitProject = async () => {
        if (submitting) return;
        
        // Determine if we're submitting a group project or individual project
        const isGroupSubmission = activeTab === 'group-projects' && groupData;
        const currentProjects = isGroupSubmission ? groupProjects : projects;
        
        const project = currentProjects.find(p => p.project_id === submissionProjectId);
        if (!project) {
            pushToast('error', 'Project not found.');
            return;
        }
        
        if (!submissionText.trim()) {
            pushToast('error', 'Describe your work before submitting.');
            return;
        }
        
        try {
            setSubmitting(true);
            
            // Calculate total file size to determine which upload method to use
            const totalFileSize = calculateTotalFileSize(submissionFiles);
            const useLargeFileUpload = totalFileSize > LARGE_FILE_THRESHOLD;

            if (useLargeFileUpload && submissionFiles.length > 0) {
                // Use signed URL upload for large files
                const response = await submitProjectWithSignedUrls(isGroupSubmission);
                
                if (response) {
                    const updatedProjectData = { 
                        submitted_at: response.submitted_at, 
                        submission: submissionText, 
                        submission_files: response.files_submitted || [] 
                    };
                    
                    // Update the project in local state
                    if (isGroupSubmission) {
                        setGroupProjects(prev => prev.map(proj => 
                            proj.project_id === submissionProjectId 
                                ? { ...proj, ...updatedProjectData }
                                : proj
                        ));
                    } else {
                        setProjects(prev => prev.map(proj => 
                            proj.project_id === submissionProjectId 
                                ? { ...proj, ...updatedProjectData }
                                : proj
                        ));
                    }
                    
                    // Update selected project if it's the one we just submitted
                    if (selectedProject?.project_id === submissionProjectId) {
                        setSelectedProject(prev => ({ ...prev, ...updatedProjectData }));
                    }
                    
                    // Close modal and show success
                    handleCloseSubmissionModal();
                    pushToast('success', isGroupSubmission ? 'Group project submitted.' : 'Project submitted.');
                }
            } else if (isGroupSubmission) {
                // Submit as group project using standard upload
                const response = await submitGroupProject(submissionProjectId, submissionText, submissionFiles);
                
                if (response) {
                    const updatedProjectData = { 
                        submitted_at: response.submitted_at, 
                        submission: submissionText, 
                        submission_files: submissionFiles.map(f => f.name) 
                    };
                    
                    // Update the group project in local state
                    setGroupProjects(prev => prev.map(proj => 
                        proj.project_id === submissionProjectId 
                            ? { ...proj, ...updatedProjectData }
                            : proj
                    ));
                    
                    // Update selected project if it's the one we just submitted
                    if (selectedProject?.project_id === submissionProjectId) {
                        setSelectedProject(prev => ({ ...prev, ...updatedProjectData }));
                    }
                    
                    // Close modal and show success
                    handleCloseSubmissionModal();
                    pushToast('success', 'Group project submitted.');
                }
            } else {
                // Submit as individual project using standard upload
                const formData = new FormData();
                formData.append('submission', submissionText);
                
                // Add files to form data
                submissionFiles.forEach((file, index) => {
                    formData.append('files', file);
                });
                
                const response = await API.post(
                    `work-trials/${workTrialId}/project/${submissionProjectId}/submit`,
                    formData
                );
                
                if (response.data) {
                    const updatedProjectData = { 
                        submitted_at: response.data.submitted_at, 
                        submission: submissionText, 
                        submission_files: submissionFiles.map(f => f.name) 
                    };
                    
                    // Update the project in local state
                    setProjects(prev => prev.map(proj => 
                        proj.project_id === submissionProjectId 
                            ? { ...proj, ...updatedProjectData }
                            : proj
                    ));
                    
                    // Update selected project if it's the one we just submitted
                    if (selectedProject?.project_id === submissionProjectId) {
                        setSelectedProject(prev => ({ ...prev, ...updatedProjectData }));
                    }
                    
                    // Close modal and show success
                    handleCloseSubmissionModal();
                    pushToast('success', 'Project submitted.');
                }
            }
            
        } catch (error) {
            console.error("Error submitting project:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else if (error.message) {
                pushToast('error', error.message);
            } else {
                pushToast('error', "Couldn't submit the project. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return formatToLocalDateTime(dateString);
    };

    const formatProjectDate = (dateString) => {
        return formatToLocalDateTime(dateString);
    };

    /*
     * Where a submission is read from now.
     *
     * `GET /work-trials/<id>/projects` answers with the eval4 per-step shape for every trial,
     * including the eval3 ones this page renders, and that shape no longer carries the
     * project-level `submitted_at` and `submission` this page was written against. A project
     * with no authored steps *is* one implicit step, and that step is where an old trial's one
     * stamp and one write-up now arrive. Read the steps first; keep the project-level fields as
     * the fallback, because the optimistic updates after a submit still write them into local
     * state and would otherwise flip a just-submitted project back to "in progress".
     *
     * A project is submitted only when every step is — which is what the project-level stamp
     * meant all along ("the last step landed"). Reading step 1 of three would report work
     * nobody filed.
     */
    const readSubmission = (project) => {
        const steps = Array.isArray(project?.steps) ? project.steps : [];
        const stamps = steps.map((step) => (step.submitted ? step.submitted_at : null));
        const allIn = stamps.length > 0 && stamps.every(Boolean);
        const latest = allIn ? stamps.slice().sort()[stamps.length - 1] : null;
        const authored = steps.find((step) => step.submission_text);

        return {
            submittedAt: latest || project?.submitted_at || null,
            text: (authored && authored.submission_text) || project?.submission || '',
            // Undefined, not empty: the server does not send this list any more, and an empty
            // array would let the modal claim nothing was filed. See the modal below.
            files: project?.submission_files,
        };
    };

    const getProjectStatus = (project) => {
        const now = new Date();
        const deadline = parseUTCToLocal(project.deadline);
        const submittedAt = parseUTCToLocal(readSubmission(project).submittedAt);

        if (submittedAt) {
            return { status: 'submitted', color: 'success', text: 'Submitted' };
        }
        
        if (deadline && now > deadline) {
            // Show "Overdue (Can Submit)" if post-deadline submissions are allowed
            if (project.allow_post_deadline_submission) {
                return { status: 'overdue-allowed', color: 'warning', text: 'Overdue' };
            }
            return { status: 'overdue', color: 'danger', text: 'Overdue' };
        }
        
        return { status: 'in-progress', color: 'warning', text: 'In progress' };
    };

    const getInterviewStatus = (interview) => {
        const now = new Date();
        const deadline = parseUTCToLocal(interview.deadline);
        const submittedAt = parseUTCToLocal(interview.submission_date);
        const startedAt = parseUTCToLocal(interview.start_date);
        
        if (submittedAt) {
            return { status: 'submitted', color: 'success', text: 'Submitted' };
        }
        
        if (deadline && now > deadline) {
            return { status: 'overdue', color: 'danger', text: 'Overdue' };
        }
        
        if (startedAt) {
            return { status: 'in-progress', color: 'warning', text: 'In progress' };
        }
        
        return { status: 'not-started', color: 'info', text: 'Not started' };
    };

    const canStartInterview = (interview) => {
        const now = new Date();
        const deadline = parseUTCToLocal(interview.deadline);
        const submittedAt = parseUTCToLocal(interview.submission_date);
        
        // Can start if not submitted and deadline hasn't passed
        return !submittedAt && (!deadline || now <= deadline);
    };

    const handleStartInterview = (interviewId) => {
        // Navigate to interview page
        navigate(`/interview/${workTrialId}/${interviewId}`);
    };

    const renderProjectsList = () => {
        const isGroupProjectsTab = activeTab === 'group-projects';
        const currentProjects = isGroupProjectsTab && groupData ? groupProjects : projects;
        const listLoading = projectsLoading || (isGroupProjectsTab && groupLoading);

        // Skeletons only on first load — refreshes keep the rows in place
        if (listLoading && currentProjects.length === 0) {
            return (
                <div className="wt-index-skeleton" role="status">
                    <span className="wt-visually-hidden">Loading projects</span>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="wt-skeleton-row" aria-hidden="true">
                            <span className="wt-skeleton-line wt-skeleton-line--title" />
                            <span className="wt-skeleton-line wt-skeleton-line--meta" />
                        </div>
                    ))}
                </div>
            );
        }

        if (currentProjects.length === 0) return null;

        return (
            <ul className="wt-index-list">
                {currentProjects.map((project, index) => {
                    const projectStatus = getProjectStatus(project);
                    const isSelected = selectedProject?.project_id === project.project_id;

                    return (
                        <li key={project.project_id || index}>
                            <button
                                type="button"
                                className={`wt-row ${isSelected ? 'is-selected' : ''}`}
                                style={{ '--wt-row-index': Math.min(index, 8) }}
                                aria-current={isSelected ? 'true' : undefined}
                                onClick={() => {
                                    setSelectedProject(project);
                                    logActivity('selection', isGroupProjectsTab ? 'select-group-project' : 'select-project', {
                                        project_id: project.project_id,
                                        project_status: projectStatus.status
                                    });
                                }}
                            >
                                <span className="wt-row-title">
                                    {project.project_name || `Project ${index + 1}`}
                                </span>
                                <span className="wt-row-meta">
                                    <span className={`wt-row-status is-${projectStatus.color}`}>
                                        <span className="wt-status-dot" aria-hidden="true" />
                                        {projectStatus.text}
                                    </span>
                                    {project.deadline && (
                                        <>
                                            <span className="wt-row-meta-sep" aria-hidden="true">·</span>
                                            <span className="wt-row-meta-item">Due {formatProjectDate(project.deadline)}</span>
                                        </>
                                    )}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    };
    
    const renderProjectDetail = () => {
        if (!selectedProject) return null;

        const projectStatus = getProjectStatus(selectedProject);
        const selectedSubmission = readSubmission(selectedProject);
        const isDownloading = downloadingFiles[selectedProject.project_id];
        const isGroupProjectsTab = activeTab === 'group-projects';
        const isOverdue = projectStatus.status === 'overdue' || projectStatus.status === 'overdue-allowed';
        const submitAllowed = (projectStatus.status === 'in-progress' || projectStatus.status === 'overdue-allowed')
            && (isGroupProjectsTab || selectedProject.opened_at);

        return (
            <div className="wt-dossier-inner">
                <button type="button" className="wt-back" onClick={handleBackToList}>
                    ← All projects
                </button>

                <header className="wt-dossier-head" data-tour="wt-dossier-head">
                    <div className="wt-dossier-heading">
                        <h3 className="wt-dossier-title">{selectedProject.project_name || 'Project'}</h3>
                        <StatusMark status={projectStatus} />
                    </div>
                    <div className="wt-dossier-actions" data-tour="wt-dossier-actions">
                        {selectedProject.task_files && selectedProject.task_files.length > 0 && (
                            <button
                                type="button"
                                className="wt-btn"
                                onClick={() => handleGetProjectFiles(selectedProject.project_id)}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <><span className="wt-spinner-sm" aria-hidden="true" /> Preparing…</>
                                ) : (
                                    'Get files'
                                )}
                            </button>
                        )}
                        {selectedSubmission.submittedAt && (
                            <button
                                type="button"
                                className="wt-btn"
                                onClick={() => handleViewSubmission(selectedProject)}
                            >
                                View submission
                            </button>
                        )}
                        {submitAllowed && (
                            <button
                                type="button"
                                className="wt-btn wt-btn--primary"
                                onClick={() => handleOpenSubmissionModal(selectedProject.project_id)}
                            >
                                Submit project
                            </button>
                        )}
                    </div>
                </header>

                {projectStatus.status === 'overdue-allowed' && (
                    <div className="wt-note wt-note--warn" role="status">
                        <WarningIcon /> This project is past its deadline — you can still submit it.
                    </div>
                )}
                {projectStatus.status === 'overdue' && (
                    <div className="wt-note wt-note--danger" role="status">
                        <WarningIcon /> The deadline for this project has passed.
                    </div>
                )}

                <div className="wt-dossier-body">
                    <section className="wt-section">
                        <h4 className="wt-eyebrow">Brief</h4>
                        <div className="wt-prose">
                            {renderTextWithLinks(selectedProject.task_description, 'No description provided yet.')}
                        </div>
                    </section>

                    <section className="wt-section">
                        <h4 className="wt-eyebrow">Details</h4>
                        <dl className="wt-facts">
                            {selectedProject.deadline && (
                                <div className="wt-fact">
                                    <dt>Deadline</dt>
                                    <dd className={isOverdue ? 'is-overdue' : undefined}>
                                        {formatProjectDate(selectedProject.deadline)}
                                    </dd>
                                </div>
                            )}

                            {selectedProject.created_at && (
                                <div className="wt-fact">
                                    <dt>Assigned</dt>
                                    <dd>{formatProjectDate(selectedProject.created_at)}</dd>
                                </div>
                            )}

                            {selectedSubmission.submittedAt && (
                                <div className="wt-fact">
                                    <dt>Submitted</dt>
                                    <dd>{formatProjectDate(selectedSubmission.submittedAt)}</dd>
                                </div>
                            )}

                            {selectedProject.max_files_needed > 0 && (
                                <div className="wt-fact">
                                    <dt>File limit</dt>
                                    <dd>{selectedProject.max_files_needed} file{selectedProject.max_files_needed !== 1 ? 's' : ''}</dd>
                                </div>
                            )}

                            {selectedProject.task_files && selectedProject.task_files.length > 0 && (
                                <div className="wt-fact">
                                    <dt>Materials</dt>
                                    <dd>{selectedProject.task_files.length} file{selectedProject.task_files.length !== 1 ? 's' : ''} — download with “Get files”</dd>
                                </div>
                            )}
                        </dl>
                    </section>

                    {selectedProject.has_github_repo && (() => {
                        const connectedUsername = isGroupProjectsTab
                            ? selectedProject.github_usernames && selectedProject.github_usernames[user?.user_id]
                            : selectedProject.github_username;

                        return (
                            <section className="wt-section">
                                <h4 className="wt-eyebrow">GitHub repository</h4>
                                {connectedUsername ? (
                                    <dl className="wt-facts">
                                        {selectedProject.github_repo && (
                                            <div className="wt-fact">
                                                <dt>Repository</dt>
                                                <dd>
                                                    <a
                                                        href={selectedProject.github_repo.startsWith('http') ? selectedProject.github_repo : `https://github.com/Appli-AI-Projects/${selectedProject.github_repo}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="wt-link"
                                                    >
                                                        {selectedProject.github_repo} <LinkIcon />
                                                    </a>
                                                </dd>
                                            </div>
                                        )}
                                        <div className="wt-fact">
                                            <dt>Access</dt>
                                            <dd className="wt-value-ok">
                                                <CheckIcon /> Granted to {connectedUsername}
                                            </dd>
                                        </div>
                                    </dl>
                                ) : (
                                    <div className="wt-note wt-note--warn">
                                        <WarningIcon />
                                        <span className="wt-note-text">Connect your GitHub username to get access to the project repository.</span>
                                        <button
                                            type="button"
                                            className="wt-btn wt-btn--small"
                                            onClick={() => handleOpenGithubSetupModal(selectedProject.project_id)}
                                        >
                                            Connect GitHub
                                        </button>
                                    </div>
                                )}
                            </section>
                        );
                    })()}

                    {(() => {
                        const driveInfo = projectDriveInfo[selectedProject.project_id];
                        const isLoading = loadingDriveInfo[selectedProject.project_id];
                        const isCreatingDoc = creatingDoc[selectedProject.project_id];
                        const isCreatingSheet = creatingSheet[selectedProject.project_id];
                        const files = driveFiles[selectedProject.project_id] || [];
                        const isLoadingFiles = loadingDriveFiles[selectedProject.project_id];
                        const isFilesExpanded = driveFilesExpanded[selectedProject.project_id];

                        const userGmail = isGroupProjectsTab
                            ? (selectedProject.drive_gmails && selectedProject.drive_gmails[user?.user_id])
                            : selectedProject.drive_gmail;

                        if (isLoading) {
                            return (
                                <section className="wt-section">
                                    <h4 className="wt-eyebrow">Google Drive</h4>
                                    <div className="wt-loading-line">
                                        <span className="wt-spinner-sm" aria-hidden="true" />
                                        Loading drive info…
                                    </div>
                                </section>
                            );
                        }

                        if (driveInfo && driveInfo.has_drive) {
                            return (
                                <section className="wt-section">
                                    <h4 className="wt-eyebrow">Google Drive</h4>
                                    <div className="wt-drive">
                                        {userGmail ? (
                                            <div className="wt-drive-account">
                                                <span className="wt-drive-account-label">Connected as</span>
                                                <span className="wt-drive-account-email">{userGmail}</span>
                                                <span className="wt-drive-account-actions">
                                                    <button
                                                        type="button"
                                                        className="wt-btn wt-btn--small"
                                                        onClick={() => handleOpenGmailSetupModal(selectedProject.project_id)}
                                                        disabled={removingGmail}
                                                    >
                                                        Change
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="wt-btn wt-btn--small wt-btn--danger"
                                                        onClick={() => setConfirmRemoveGmailId(selectedProject.project_id)}
                                                        disabled={removingGmail}
                                                    >
                                                        {removingGmail ? 'Removing…' : 'Remove'}
                                                    </button>
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="wt-note wt-note--warn">
                                                <WarningIcon />
                                                <span className="wt-note-text">Add your email to access the drive (optional if your account email is a Gmail).</span>
                                                <button
                                                    type="button"
                                                    className="wt-btn wt-btn--small"
                                                    onClick={() => handleOpenGmailSetupModal(selectedProject.project_id)}
                                                >
                                                    Add email
                                                </button>
                                            </div>
                                        )}

                                        <dl className="wt-facts">
                                            <div className="wt-fact">
                                                <dt>Folder</dt>
                                                <dd>
                                                    <a
                                                        href={driveInfo.drive_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="wt-link"
                                                    >
                                                        Open Drive folder <LinkIcon />
                                                    </a>
                                                </dd>
                                            </div>
                                            <div className="wt-fact">
                                                <dt>Access</dt>
                                                <dd className={`wt-access-${driveInfo.access_level || 'none'}`}>
                                                    {driveInfo.access_level === 'edit' ? <><EditIcon /> Can edit</> :
                                                     driveInfo.access_level === 'view' ? <><EyeIcon /> View only</> :
                                                     <><BlockIcon /> No access</>}
                                                </dd>
                                            </div>
                                        </dl>

                                        {driveInfo.access_level === 'edit' && (
                                            <div className="wt-drive-create">
                                                <button
                                                    type="button"
                                                    className="wt-btn"
                                                    onClick={() => handleCreateDoc(selectedProject.project_id, isGroupProjectsTab)}
                                                    disabled={isCreatingDoc || isCreatingSheet}
                                                    title="Create a new Google Doc in this project's drive"
                                                >
                                                    {isCreatingDoc ? (
                                                        <><span className="wt-spinner-sm" aria-hidden="true" /> Creating…</>
                                                    ) : (
                                                        <>
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                <path d="M14 2V8H20" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                <path d="M16 13H8" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                <path d="M16 17H8" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                            New Doc
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="wt-btn"
                                                    onClick={() => handleCreateSheet(selectedProject.project_id, isGroupProjectsTab)}
                                                    disabled={isCreatingDoc || isCreatingSheet}
                                                    title="Create a new Google Sheet in this project's drive"
                                                >
                                                    {isCreatingSheet ? (
                                                        <><span className="wt-spinner-sm" aria-hidden="true" /> Creating…</>
                                                    ) : (
                                                        <>
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#0F9D58" strokeWidth="2"/>
                                                                <path d="M3 9H21" stroke="#0F9D58" strokeWidth="2"/>
                                                                <path d="M3 15H21" stroke="#0F9D58" strokeWidth="2"/>
                                                                <path d="M9 3V21" stroke="#0F9D58" strokeWidth="2"/>
                                                                <path d="M15 3V21" stroke="#0F9D58" strokeWidth="2"/>
                                                            </svg>
                                                            New Sheet
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {driveInfo.access_level === 'view' && (
                                            <div className="wt-note wt-note--info">
                                                <InfoIcon />
                                                <span className="wt-note-text">You have view-only access — this project has been submitted.</span>
                                            </div>
                                        )}

                                        <div className="wt-tree">
                                            <button
                                                type="button"
                                                className="wt-tree-head"
                                                onClick={() => toggleDriveFilesExpanded(selectedProject.project_id, isGroupProjectsTab)}
                                                aria-expanded={!!isFilesExpanded}
                                            >
                                                <span className={`wt-tree-caret ${isFilesExpanded ? 'is-open' : ''}`} aria-hidden="true">
                                                    <ChevronDownIcon />
                                                </span>
                                                Files in Drive
                                                {isLoadingFiles && <span className="wt-spinner-sm" aria-hidden="true" />}
                                            </button>

                                            {isFilesExpanded && (
                                                <div className="wt-tree-body">
                                                    {isLoadingFiles ? (
                                                        <div className="wt-tree-note">Loading files…</div>
                                                    ) : files.length === 0 ? (
                                                        <div className="wt-tree-note">No files yet</div>
                                                    ) : (
                                                        <ul className="wt-tree-list">
                                                            {files.map((file, index) => (
                                                                <li key={file.id || index}>
                                                                    {file.webViewLink ? (
                                                                        <a
                                                                            href={file.webViewLink}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="wt-tree-item"
                                                                        >
                                                                            <span className="wt-tree-icon">
                                                                                {file.isFolder ? <FolderIcon /> :
                                                                                 file.mimeType?.includes('document') ? <DocumentIcon /> :
                                                                                 file.mimeType?.includes('spreadsheet') ? <SpreadsheetIcon /> :
                                                                                 file.mimeType?.includes('presentation') ? <PresentationIcon /> :
                                                                                 file.mimeType?.includes('image') ? <ImageFileIcon /> :
                                                                                 file.mimeType?.includes('pdf') ? <BookIcon /> :
                                                                                 <PaperclipIcon />}
                                                                            </span>
                                                                            <span className="wt-tree-name">{file.name}</span>
                                                                        </a>
                                                                    ) : (
                                                                        <span className="wt-tree-item wt-tree-item--static">
                                                                            <span className="wt-tree-icon">
                                                                                {file.isFolder ? <FolderIcon /> : <PaperclipIcon />}
                                                                            </span>
                                                                            <span className="wt-tree-name">{file.name}</span>
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="wt-btn wt-btn--small wt-tree-refresh"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            fetchDriveFiles(selectedProject.project_id, isGroupProjectsTab);
                                                        }}
                                                        disabled={isLoadingFiles}
                                                    >
                                                        <RefreshIcon /> Refresh
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            );
                        }

                        // Project has a drive but the user hasn't connected an email yet
                        if (selectedProject.has_drive) {
                            return (
                                <section className="wt-section">
                                    <h4 className="wt-eyebrow">Google Drive</h4>
                                    <div className="wt-note wt-note--warn">
                                        <WarningIcon />
                                        <span className="wt-note-text">Add your email to access the project drive (optional if your account email is a Gmail).</span>
                                        <button
                                            type="button"
                                            className="wt-btn wt-btn--small"
                                            onClick={() => handleOpenGmailSetupModal(selectedProject.project_id)}
                                        >
                                            Add email
                                        </button>
                                    </div>
                                </section>
                            );
                        }

                        return null;
                    })()}
                </div>
            </div>
        );
    };
    
    const renderInterviewsList = () => {
        if (interviewsLoading && interviews.length === 0) {
            return (
                <div className="wt-index-skeleton" role="status">
                    <span className="wt-visually-hidden">Loading interviews</span>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="wt-skeleton-row" aria-hidden="true">
                            <span className="wt-skeleton-line wt-skeleton-line--title" />
                            <span className="wt-skeleton-line wt-skeleton-line--meta" />
                        </div>
                    ))}
                </div>
            );
        }

        if (interviews.length === 0) return null;

        return (
            <ul className="wt-index-list">
                {interviews.map((interview, index) => {
                    const interviewStatus = getInterviewStatus(interview);
                    const isSelected = selectedInterview?.interview_id === interview.interview_id;
                    const questionCount = interview.questions ? Object.keys(interview.questions).length : 0;

                    return (
                        <li key={interview.interview_id || index}>
                            <button
                                type="button"
                                className={`wt-row ${isSelected ? 'is-selected' : ''}`}
                                style={{ '--wt-row-index': Math.min(index, 8) }}
                                aria-current={isSelected ? 'true' : undefined}
                                onClick={() => {
                                    setSelectedInterview(interview);
                                    logActivity('selection', 'select-interview', {
                                        interview_id: interview.interview_id,
                                        interview_status: interviewStatus.status
                                    });
                                }}
                            >
                                <span className="wt-row-title">
                                    {interview.interview_name || `Interview ${index + 1}`}
                                </span>
                                <span className="wt-row-meta">
                                    <span className={`wt-row-status is-${interviewStatus.color}`}>
                                        <span className="wt-status-dot" aria-hidden="true" />
                                        {interviewStatus.text}
                                    </span>
                                    {interview.deadline && (
                                        <>
                                            <span className="wt-row-meta-sep" aria-hidden="true">·</span>
                                            <span className="wt-row-meta-item">Due {formatProjectDate(interview.deadline)}</span>
                                        </>
                                    )}
                                    {questionCount > 0 && (
                                        <>
                                            <span className="wt-row-meta-sep" aria-hidden="true">·</span>
                                            <span className="wt-row-meta-item">{questionCount} question{questionCount !== 1 ? 's' : ''}</span>
                                        </>
                                    )}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderInterviewDetail = () => {
        if (!selectedInterview) return null;

        const interviewStatus = getInterviewStatus(selectedInterview);
        const canStart = canStartInterview(selectedInterview);
        const isOverdue = interviewStatus.status === 'overdue';
        const questionCount = selectedInterview.questions ? Object.keys(selectedInterview.questions).length : 0;

        return (
            <div className="wt-dossier-inner">
                <button type="button" className="wt-back" onClick={handleBackToList}>
                    ← All interviews
                </button>

                <header className="wt-dossier-head" data-tour="wt-dossier-head">
                    <div className="wt-dossier-heading">
                        <h3 className="wt-dossier-title">{selectedInterview.interview_name || 'Interview'}</h3>
                        <StatusMark status={interviewStatus} />
                    </div>
                    <div className="wt-dossier-actions" data-tour="wt-dossier-actions">
                        {canStart && (
                            <button
                                type="button"
                                className="wt-btn wt-btn--primary"
                                onClick={() => handleStartInterview(selectedInterview.interview_id)}
                            >
                                Start interview
                            </button>
                        )}
                    </div>
                </header>

                {isOverdue && (
                    <div className="wt-note wt-note--danger" role="status">
                        <WarningIcon /> The deadline for this interview has passed.
                    </div>
                )}

                <div className="wt-dossier-body">
                    <section className="wt-section">
                        <h4 className="wt-eyebrow">Details</h4>
                        <dl className="wt-facts">
                            {questionCount > 0 && (
                                <div className="wt-fact">
                                    <dt>Questions</dt>
                                    <dd>{questionCount} interactive question{questionCount !== 1 ? 's' : ''}</dd>
                                </div>
                            )}

                            {selectedInterview.deadline && (
                                <div className="wt-fact">
                                    <dt>Deadline</dt>
                                    <dd className={isOverdue ? 'is-overdue' : undefined}>
                                        {formatProjectDate(selectedInterview.deadline)}
                                    </dd>
                                </div>
                            )}

                            {selectedInterview.release_date && (
                                <div className="wt-fact">
                                    <dt>Released</dt>
                                    <dd>{formatProjectDate(selectedInterview.release_date)}</dd>
                                </div>
                            )}

                            {selectedInterview.start_date && (
                                <div className="wt-fact">
                                    <dt>Started</dt>
                                    <dd>{formatProjectDate(selectedInterview.start_date)}</dd>
                                </div>
                            )}

                            {selectedInterview.submission_date && (
                                <div className="wt-fact">
                                    <dt>Submitted</dt>
                                    <dd>{formatProjectDate(selectedInterview.submission_date)}</dd>
                                </div>
                            )}
                        </dl>
                    </section>

                    {canStart && (
                        <section className="wt-section">
                            <h4 className="wt-eyebrow">Before you start</h4>
                            <p className="wt-prose wt-prose--muted">
                                Find a quiet spot with a stable connection. Once you start, answer each
                                question in order — you can review everything before submitting.
                            </p>
                        </section>
                    )}
                </div>
            </div>
        );
    };

    const handleViewSubmission = (project) => {
        setViewingProject(project);
        setShowViewSubmissionModal(true);
    };

    const handleCloseViewSubmissionModal = () => {
        setShowViewSubmissionModal(false);
        setViewingProject(null);
    };

    const handleDownloadSubmittedFile = async (fileName) => {
        try {
            // Determine if this is a group project or individual project
            const isGroupProject = groupProjects && groupProjects.some(p => p.project_id === viewingProject.project_id);
            
            let apiEndpoint;
            if (isGroupProject) {
                apiEndpoint = `work-trials/${workTrialId}/group/project/${viewingProject.project_id}/submission-file/${encodeURIComponent(fileName)}`;
            } else {
                apiEndpoint = `work-trials/${workTrialId}/project/${viewingProject.project_id}/submission-file/${encodeURIComponent(fileName)}`;
            }
            
            const response = await API.get(apiEndpoint);
            
            if (response.data && response.data.download_url) {
                // Create a download link using the signed URL
                const link = document.createElement('a');
                link.href = response.data.download_url;
                link.download = response.data.original_name || fileName.split('/').pop();
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                pushToast('error', "Couldn't get a download link. Please try again.");
            }
        } catch (error) {
            console.error("Error downloading file:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't download the file. Please try again.");
            }
        }
    };

    const handleTabSwitch = (tab) => {
        setActiveTab(tab);

        // Mobile is list-first: a selection carried across tabs would open the
        // wrong dossier (and route actions to the wrong endpoints), so clear it.
        if (window.innerWidth <= 768 && tab !== activeTab) {
            setSelectedProject(null);
            setSelectedInterview(null);
        }

        // Clear unread count when switching to chat tab
        if (tab === 'chat') {
            setUnreadCount(0);
            setGroupChatMode(false);
        }
        
        // Set group chat mode when switching to group-chat tab
        if (tab === 'group-chat') {
            setGroupChatMode(true);
        }
        
        // Log navigation based on tab
        if (tab === 'chat') {
                logActivity('navigation', 'individual-chat');
        } else if (tab === 'group-chat') {
            logActivity('navigation', 'group-chat');
        } else if (tab === 'projects') {
                logActivity('navigation', 'project-individual');
        } else if (tab === 'group-projects') {
            logActivity('navigation', 'project-group');
        } else if (tab === 'interviews') {
            logActivity('navigation', 'interview');
        }
    };

    const toggleSound = () => {
        setSoundEnabled(prev => !prev);
    };

    const handleNotificationSettings = () => {
        if (notificationPermission === 'denied') {
            pushToast('info', 'Notifications are blocked — enable them in your browser settings for this site.');
        } else if (notificationPermission === 'default') {
            requestNotificationPermission();
        } else {
            pushToast('info', "Notifications are on. You'll hear about new messages while you're away from the chat.");
        }
    };

    const handleManualRefresh = async () => {
        setRefreshError('');
        try {
            // Refresh all data types in parallel
            const promises = [
                fetchProjects(),
                fetchInterviews()
            ];
            
            // Also refresh group data if user is in a group
            if (groupData) {
                promises.push(fetchGroupData());
            }
            
            await Promise.all(promises);
        } catch (error) {
            console.error('Error during manual refresh:', error);
            setRefreshError('Failed to refresh data. Please try again.');
        }
    };

    const dismissNotificationBanner = () => {
        setShowNotificationBanner(false);
        localStorage.setItem('notification-banner-dismissed', 'true');
    };

    const enableNotificationsFromBanner = async () => {
        const granted = await requestNotificationPermission();
        if (granted) {
            setShowNotificationBanner(false);
        }
    };

    const handleOpenGithubSetupModal = (projectId) => {
        setGithubSetupProjectId(projectId);
        setGithubUsername('');
        setShowGithubSetupModal(true);
    };

    const handleCloseGithubSetupModal = () => {
        if (settingUpGithub) return; // Prevent closing while setting up
        
        setShowGithubSetupModal(false);
        setGithubSetupProjectId(null);
        setGithubUsername('');
    };

    const handleSetupGithub = async () => {
        if (settingUpGithub) return;
        
        if (!githubUsername.trim()) {
            pushToast('error', 'Enter your GitHub username first.');
            return;
        }
        
        try {
            setSettingUpGithub(true);
            
            // Determine if we're setting up for a group project or individual project
            const isGroupProject = activeTab === 'group-projects' && groupData;
            const currentProjects = isGroupProject ? groupProjects : projects;
            const project = currentProjects.find(p => p.project_id === githubSetupProjectId);
            
            if (!project) {
                pushToast('error', 'Project not found.');
                return;
            }
            
            let apiEndpoint;
            if (isGroupProject) {
                apiEndpoint = `work-trials/${workTrialId}/group/project/${githubSetupProjectId}/setup-github`;
            } else {
                apiEndpoint = `work-trials/${workTrialId}/project/${githubSetupProjectId}/setup-github`;
            }
            
            const response = await API.post(
                apiEndpoint,
                { github_username: githubUsername.trim() }
            );
            
            if (response.data) {
                // Update the project in local state with the GitHub username and repo info
                if (isGroupProject) {
                    setGroupProjects(prev => prev.map(proj => 
                        proj.project_id === githubSetupProjectId 
                            ? { 
                                ...proj, 
                                github_usernames: {
                                    ...proj.github_usernames,
                                    [user.user_id]: githubUsername.trim()
                                },
                                github_repo: response.data.repo_url || response.data.repo_name || proj.github_repo
                            }
                            : proj
                    ));
                    
                    // Update selected project if it's the one we just updated
                    if (selectedProject?.project_id === githubSetupProjectId) {
                        setSelectedProject(prev => ({
                            ...prev,
                            github_usernames: {
                                ...prev.github_usernames,
                                [user.user_id]: githubUsername.trim()
                            },
                            github_repo: response.data.repo_url || response.data.repo_name || prev.github_repo
                        }));
                    }
                } else {
                    setProjects(prev => prev.map(proj => 
                        proj.project_id === githubSetupProjectId 
                            ? { 
                                ...proj, 
                                github_username: githubUsername.trim(),
                                github_repo: response.data.repo_url || response.data.repo_name || proj.github_repo
                            }
                            : proj
                    ));
                    
                    // Update selected project if it's the one we just updated
                    if (selectedProject?.project_id === githubSetupProjectId) {
                        setSelectedProject(prev => ({
                            ...prev,
                            github_username: githubUsername.trim(),
                            github_repo: response.data.repo_url || response.data.repo_name || prev.github_repo
                        }));
                    }
                }
                
                // Close modal and show success
                handleCloseGithubSetupModal();
                pushToast('success', 'GitHub access granted.');
            }
            
        } catch (error) {
            console.error("Error setting up GitHub:", error);
            if (error.response?.data?.error) {
                pushToast('error', error.response.data.error);
            } else {
                pushToast('error', "Couldn't connect GitHub. Please try again.");
            }
        } finally {
            setSettingUpGithub(false);
        }
    };

    const markdownComponents = {
        h1: ({children}) => <h1 className="markdown-h1">{children}</h1>,
        h2: ({children}) => <h2 className="markdown-h2">{children}</h2>,
        h3: ({children}) => <h3 className="markdown-h3">{children}</h3>,
        p: ({children}) => <p className="markdown-p">{children}</p>,
        ul: ({children}) => <ul className="markdown-ul">{children}</ul>,
        ol: ({children}) => <ol className="markdown-ol">{children}</ol>,
        li: ({children}) => <li className="markdown-li">{children}</li>,
        code: ({children, className, inline}) => {
            if (inline) {
                return <code className="markdown-code-inline">{children}</code>;
            }
            const language = className ? className.replace('language-', '') : '';
            return (
                <code className={`markdown-code-block ${className || ''}`} data-language={language}>
                    {children}
                </code>
            );
        },
        pre: ({children}) => <pre className="markdown-pre">{children}</pre>,
        blockquote: ({children}) => <blockquote className="markdown-blockquote">{children}</blockquote>,
        strong: ({children}) => <strong className="markdown-strong">{children}</strong>,
        em: ({children}) => <em className="markdown-em">{children}</em>,
        hr: () => <hr className="markdown-hr" />,
        table: ({children}) => <table className="markdown-table">{children}</table>,
        thead: ({children}) => <thead className="markdown-thead">{children}</thead>,
        tbody: ({children}) => <tbody className="markdown-tbody">{children}</tbody>,
        tr: ({children}) => <tr className="markdown-tr">{children}</tr>,
        th: ({children}) => <th className="markdown-th">{children}</th>,
        td: ({children}) => <td className="markdown-td">{children}</td>,
        a: ({children, href}) => (
            <a
                href={href}
                className="markdown-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </a>
        ),
    };

    const isIndividualUserMessage = (message) =>
        message.sender === 'user' || message.sender === user?.user_id;

    const renderIndividualMessageBody = (message) => {
        if (isIndividualUserMessage(message)) {
            return renderTextWithLinks(message.message);
        }
        return <ReactMarkdown components={markdownComponents}>{message.message}</ReactMarkdown>;
    };

    const individualPending = pendingSends.filter(p => p.mode === 'individual');
    const groupPending = pendingSends.filter(p => p.mode === 'group');

    const tabs = [
        { id: 'chat', label: 'Chat', badge: activeTab !== 'chat' ? unreadCount : 0 },
        ...(groupData ? [{ id: 'group-chat', label: 'Group chat' }] : []),
        { id: 'projects', label: 'Projects' },
        ...(groupData ? [{ id: 'group-projects', label: 'Group projects' }] : []),
        { id: 'interviews', label: 'Interviews' },
    ];

    const handleTabKeyDown = (event) => {
        const ids = tabs.map((t) => t.id);
        const idx = ids.indexOf(activeTab);
        let next = null;
        if (event.key === 'ArrowRight') next = ids[(idx + 1) % ids.length];
        else if (event.key === 'ArrowLeft') next = ids[(idx - 1 + ids.length) % ids.length];
        else if (event.key === 'Home') next = ids[0];
        else if (event.key === 'End') next = ids[ids.length - 1];
        if (next) {
            event.preventDefault();
            handleTabSwitch(next);
            tabRefs.current[next]?.focus();
        }
    };

    const refreshBusy = projectsLoading || interviewsLoading || groupLoading;

    // Where a job trial names the company hiring for it, a cycle trial names the
    // structure it runs and the version of it. Everything below renders from
    // these two; there is no second page for a cycle.
    const isCycle = isCycleTrial(workTrial);
    const framing = getTrialFraming(workTrial);

    // Per-tab first-visit tour steps, snapshotted for the tour's lifetime —
    // rebuilding them from live state mid-tour could shrink the array under
    // GuidedTour's feet (and would reset its observers every render).
    const tabTourSteps = useMemo(() => {
        if (!activeTabTour) return null;
        switch (activeTabTour) {
            case 'projects':
                return [
                    { target: '[data-tour="wt-index"]', title: 'Your projects', description: 'Every project assigned to you is listed here, with its status and deadline at a glance.', placement: 'bottom-start' },
                    { target: '[data-tour="wt-dossier-head"]', title: 'The project brief', description: 'The full brief, key dates, and any files or repository access for the selected project live here. Download materials and submit your work with the buttons on the right.', placement: 'bottom' },
                ];
            case 'group-chat': {
                const steps = [];
                if (!myAliasSet) {
                    steps.push({ target: '.alias-card', title: 'Pick a group name', description: 'Teammates see this alias instead of your real name, so everyone stays anonymous. Choose carefully — it can\'t be changed later.', placement: 'bottom' });
                } else {
                    steps.push({ target: '[data-tour="wt-group-composer"]', title: 'Talk to your group', description: 'Messages here go to everyone in your group. You appear under your chosen alias.', placement: 'top' });
                }
                if (groupMembers && groupMembers.length > 0) {
                    steps.push({ target: '[data-tour="wt-members"]', title: 'Your group', description: 'See who you\'re working with. Everyone appears under their chosen alias.', placement: 'bottom-end' });
                }
                return steps;
            }
            case 'group-projects':
                return [
                    { target: '[data-tour="wt-index"]', title: 'Group projects', description: 'Projects your group works on together are listed here.', placement: 'bottom-start' },
                    { target: '[data-tour="wt-dossier-head"]', title: 'Shared brief', description: 'The brief, shared files, and submission for the selected group project live here.', placement: 'bottom' },
                ];
            case 'interviews': {
                const steps = [
                    { target: '[data-tour="wt-index"]', title: 'Your interviews', description: 'Interviews assigned to you are listed here with their deadlines.', placement: 'bottom-start' },
                    { target: '[data-tour="wt-dossier-head"]', title: 'Interview details', description: 'Check the number of questions and the deadline before you begin.', placement: 'bottom' },
                ];
                if (selectedInterview && canStartInterview(selectedInterview)) {
                    steps.push({ target: '[data-tour="wt-dossier-actions"]', title: 'Start when ready', description: 'The interview begins as soon as you press this — make sure you have a quiet moment first.', placement: 'bottom-end' });
                }
                return steps;
            }
            default:
                return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabTour]);

    if (loading) {
        return (
            <>
                <Navigation />
                <div className="wt-page">
                    <div className="wt-page-state">
                        <span className="wt-spinner" aria-hidden="true" />
                        <p>Loading work trial…</p>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navigation />
                <div className="wt-page">
                    <div className="wt-page-state" role="alert">
                        <h2>Something went wrong</h2>
                        <p>{error}</p>
                        <button
                            type="button"
                            className="wt-btn wt-btn--primary"
                            onClick={() => navigate('/')}
                        >
                            Back to dashboard
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navigation />
            <div className="wt-page">
                <div className="wt-shell">

                    {/* Header */}
                    <header className="wt-header" data-tour="wt-header" data-source={framing.source}>
                        {isCycle ? (
                            <div className="wt-kicker" data-testid="wt-cycle-kicker">
                                <span className="wt-company">{framing.eyebrow}</span>
                            </div>
                        ) : workTrial?.company ? (
                            <div className="wt-company">{workTrial.company}</div>
                        ) : null}
                        <h1 className="wt-title">{workTrial?.title || framing.fallbackTitle}</h1>
                        {isCycle && (
                            <CycleTimeline
                                workTrial={workTrial}
                                timeZone={cycleContext?.display_timezone}
                            />
                        )}
                    </header>

                    <div className="wt-main">
                        {refreshError && (
                            <div className="wt-note wt-note--warn wt-note--dismissable" role="alert">
                                <WarningIcon />
                                <span className="wt-note-text">{refreshError}</span>
                                <button
                                    type="button"
                                    className="wt-icon-btn wt-icon-btn--small"
                                    onClick={() => setRefreshError('')}
                                    aria-label="Dismiss"
                                >
                                    <CloseIcon />
                                </button>
                            </div>
                        )}

                        {/* Tab rail + toolbar */}
                        <div className="wt-tabbar">
                            <div
                                className="wt-tabs"
                                role="tablist"
                                aria-label="Work trial sections"
                                ref={tabRailRef}
                                onKeyDown={handleTabKeyDown}
                                data-tour="wt-tabs"
                            >
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        ref={(el) => { tabRefs.current[tab.id] = el; }}
                                        type="button"
                                        role="tab"
                                        id={`wt-tab-${tab.id}`}
                                        aria-selected={activeTab === tab.id}
                                        aria-controls={`wt-panel-${tab.id}`}
                                        tabIndex={activeTab === tab.id ? 0 : -1}
                                        className={`wt-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                                        aria-label={tab.badge > 0 ? `${tab.label}, ${tab.badge} unread message${tab.badge !== 1 ? 's' : ''}` : undefined}
                                        onClick={() => handleTabSwitch(tab.id)}
                                    >
                                        {tab.label}
                                        {tab.badge > 0 && (
                                            <span className="wt-tab-badge" aria-hidden="true">{tab.badge}</span>
                                        )}
                                    </button>
                                ))}
                                <span
                                    className={`wt-tab-indicator ${tabIndicator.ready ? 'is-ready' : ''}`}
                                    style={{ transform: `translateX(${tabIndicator.left}px)`, width: tabIndicator.width }}
                                    aria-hidden="true"
                                />
                            </div>

                            <div className="wt-toolbar" ref={menuAreaRef} data-tour="wt-controls">
                                <button
                                    type="button"
                                    className={`wt-icon-btn ${refreshBusy ? 'is-busy' : ''}`}
                                    onClick={handleManualRefresh}
                                    disabled={refreshBusy}
                                    aria-label="Refresh data"
                                    title="Refresh data"
                                >
                                    <RefreshIcon />
                                </button>

                                {(activeTab === 'chat' || activeTab === 'group-chat') && (
                                    <>
                                        <button
                                            type="button"
                                            className={`wt-icon-btn ${soundEnabled ? '' : 'is-off'}`}
                                            onClick={toggleSound}
                                            aria-pressed={soundEnabled}
                                            aria-label={soundEnabled ? 'Turn off sound notifications' : 'Turn on sound notifications'}
                                            title={soundEnabled ? 'Sound notifications on' : 'Sound notifications off'}
                                        >
                                            {soundEnabled ? <SoundOnIcon /> : <SoundOffIcon />}
                                        </button>
                                        <button
                                            type="button"
                                            className={`wt-icon-btn ${notificationPermission === 'granted' ? '' : 'is-off'}`}
                                            onClick={handleNotificationSettings}
                                            aria-label={
                                                notificationPermission === 'granted'
                                                    ? 'Browser notifications on'
                                                    : notificationPermission === 'denied'
                                                        ? 'Browser notifications blocked'
                                                        : 'Turn on browser notifications'
                                            }
                                            title={
                                                notificationPermission === 'granted'
                                                    ? 'Browser notifications on'
                                                    : notificationPermission === 'denied'
                                                        ? 'Browser notifications blocked'
                                                        : 'Turn on browser notifications'
                                            }
                                        >
                                            {notificationPermission === 'denied' ? <BellOffIcon /> : <BellIcon />}
                                        </button>
                                    </>
                                )}

                                {groupMembers && groupMembers.length > 0 && (
                                    <div className="wt-menu">
                                        <button
                                            type="button"
                                            className={`wt-chip-btn ${openMenu === 'members' ? 'is-open' : ''}`}
                                            onClick={() => setOpenMenu(openMenu === 'members' ? null : 'members')}
                                            aria-expanded={openMenu === 'members'}
                                            data-tour="wt-members"
                                        >
                                            <GroupIcon />
                                            {groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''}
                                            <ChevronDownIcon />
                                        </button>
                                        {openMenu === 'members' && (
                                            <div className="wt-popover" aria-label="Group members">
                                                <div className="wt-popover-title">Your group</div>
                                                <ul className="wt-popover-list">
                                                    {groupMembers.map((member, idx) => (
                                                        <li key={idx} className="wt-popover-row">
                                                            <span className="wt-member-avatar" aria-hidden="true">
                                                                {(member.alias || '?').charAt(0).toUpperCase()}
                                                            </span>
                                                            <span className="wt-member-name">{member.alias}</span>
                                                            {member.is_me && <span className="wt-you-chip">You</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="wt-menu">
                                    <button
                                        type="button"
                                        className={`wt-icon-btn ${openMenu === 'settings' ? 'is-open' : ''}`}
                                        onClick={() => setOpenMenu(openMenu === 'settings' ? null : 'settings')}
                                        aria-expanded={openMenu === 'settings'}
                                        aria-label="Trial details"
                                        title="Trial details"
                                    >
                                        <DotsIcon />
                                    </button>
                                    {openMenu === 'settings' && (
                                        <div className="wt-popover" aria-label="Trial details">
                                            <div className="wt-popover-title">Trial details</div>
                                            <dl className="wt-popover-facts">
                                                <div className="wt-popover-fact">
                                                    <dt>Started</dt>
                                                    <dd>{workTrial?.started_at ? formatDate(workTrial.started_at) : 'Not started'}</dd>
                                                </div>
                                                {workTrial?.last_message_at && (
                                                    <div className="wt-popover-fact">
                                                        <dt>Last activity</dt>
                                                        <dd>{formatDate(workTrial.last_message_at)}</dd>
                                                    </div>
                                                )}
                                            </dl>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="wt-content">

                            {/* Chat — individual */}
                            {activeTab === 'chat' && (
                                <div
                                    className="wt-pane wt-chatpane"
                                    role="tabpanel"
                                    id="wt-panel-chat"
                                    aria-labelledby="wt-tab-chat"
                                    data-tour="wt-chat"
                                >
                                    
                                    {/* Notification Permission Banner */}
                                    {showNotificationBanner && notificationPermission !== 'granted' && (
                                        <div className="notification-permission-banner">
                                            <div className="banner-text">
                                                <span><BellIcon /></span>
                                                Turn on notifications to hear about new messages while you're away.
                                            </div>
                                            <div className="banner-actions">
                                                <button onClick={enableNotificationsFromBanner}>
                                                    Turn on
                                                </button>
                                                <button onClick={dismissNotificationBanner}>
                                                    Not now
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {chatConnectionError && (
                                        <div className="chat-connection-error" role="alert">
                                            {chatConnectionError}
                                        </div>
                                    )}

                                    {/* Chat Messages */}
                                    <div className="chat-container" ref={chatContainerRef} onScroll={handleChatScroll}>
                                        {chatLoading ? (
                                            <div className="chat-loading">
                                                <span className="wt-spinner-sm" aria-hidden="true"></span>
                                                <p>Loading conversation…</p>
                                            </div>
                                        ) : chatMessages.length === 0 && individualPending.length === 0 ? (
                                            <div className="chat-empty">
                                                <div className="welcome-message">
                                                    <h3>Welcome to your work trial</h3>
                                                    <p>{framing.chatIntro}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <ChatMessages
                                                messages={chatMessages}
                                                isMine={isIndividualUserMessage}
                                                senderLabel={() => 'AI Assistant'}
                                                senderIcon={() => <RobotIcon />}
                                                variantFor={() => 'ai'}
                                                renderBody={renderIndividualMessageBody}
                                                pending={individualPending}
                                            />
                                        )}
                                    </div>

                                    <NewMessagesPill visible={showNewMessagesPill} onClick={jumpToLatest} />

                                    <MessageComposer
                                        tourId="wt-message-input"
                                        value={individualDraft}
                                        onChange={setIndividualDraft}
                                        onSend={handleSendMessage}
                                        sending={sending}
                                        placeholder="Type your message…"
                                        error={individualSendError?.message || null}
                                        onRetry={handleRetrySend}
                                        onDismissError={() => setIndividualSendError(null)}
                                    />
                                </div>
                            )}

                            {/* Group chat */}
                            {activeTab === 'group-chat' && groupData && (
                                <div
                                    className="wt-pane wt-chatpane"
                                    role="tabpanel"
                                    id="wt-panel-group-chat"
                                    aria-labelledby="wt-tab-group-chat"
                                >
                                    {chatConnectionError && (
                                        <div className="chat-connection-error" role="alert">
                                            {chatConnectionError}
                                        </div>
                                    )}

                                    {/* Chat Messages */}
                                    <div className="chat-container" ref={chatContainerRef} onScroll={handleChatScroll}>
                                        {!myAliasSet ? (
                                            <div className="chat-empty">
                                                <div className="welcome-message alias-card">
                                                    <h3>Choose your group name</h3>
                                                    <p>This is the name your teammates will see in the group chat and projects — it keeps your real identity private. <strong>You can't change it later</strong>, so choose carefully.</p>
                                                    <input
                                                        type="text"
                                                        className="alias-input"
                                                        value={aliasInput}
                                                        onChange={(e) => { setAliasInput(e.target.value); setAliasError(''); }}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') submitAlias(); }}
                                                        placeholder="e.g. Blue Fox"
                                                        maxLength={24}
                                                        autoFocus
                                                    />
                                                    {aliasError && <div className="alias-error">{aliasError}</div>}
                                                    <button
                                                        type="button"
                                                        className="wt-btn wt-btn--primary alias-submit"
                                                        onClick={submitAlias}
                                                        disabled={aliasSubmitting}
                                                    >
                                                        {aliasSubmitting ? 'Saving…' : 'Use this name'}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : groupLoading ? (
                                            <div className="chat-loading">
                                                <span className="wt-spinner-sm" aria-hidden="true"></span>
                                                <p>Loading group conversation…</p>
                                            </div>
                                        ) : groupMessages.length === 0 && groupPending.length === 0 ? (
                                            <div className="chat-empty">
                                                <div className="welcome-message">
                                                    <h3>Your group chat</h3>
                                                    <p>Messages here go to everyone in your group. Use the members button above to see who you're working with.</p>
                                                    {myAlias && (
                                                        <p className="welcome-alias">You appear to your teammates as “{myAlias}”.</p>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <ChatMessages
                                                messages={groupMessages}
                                                isMine={(message) => message.sender === myAlias}
                                                senderLabel={(message) => message.sender}
                                                variantFor={() => 'peer'}
                                                renderBody={(message) => renderTextWithLinks(message.message)}
                                                pending={groupPending}
                                            />
                                        )}
                                    </div>

                                    <NewMessagesPill visible={showNewMessagesPill} onClick={jumpToLatest} />

                                    <MessageComposer
                                        tourId="wt-group-composer"
                                        value={groupDraft}
                                        onChange={setGroupDraft}
                                        onSend={handleSendMessage}
                                        sending={sending}
                                        disabled={!myAliasSet}
                                        placeholder={myAliasSet ? "Type a message to your group…" : "Choose a group name above to start chatting"}
                                        error={groupSendError?.message || null}
                                        onRetry={handleRetrySend}
                                        onDismissError={() => setGroupSendError(null)}
                                    />
                                </div>
                            )}

                            {/* Projects — individual */}
                            {activeTab === 'projects' && (
                                <div
                                    className="wt-pane"
                                    role="tabpanel"
                                    id="wt-panel-projects"
                                    aria-labelledby="wt-tab-projects"
                                >
                                    {!projectsLoading && projects.length === 0 ? (
                                        <div className="wt-empty-stage">
                                            <span className="wt-empty-icon" aria-hidden="true"><FolderIcon block /></span>
                                            <h3>No projects yet</h3>
                                            <p>{framing.projectsEmpty}</p>
                                        </div>
                                    ) : (
                                        <div className={`wt-split ${selectedProject ? 'has-detail' : ''}`}>
                                            <aside className="wt-index" data-tour="wt-index">
                                                <div className="wt-index-head">
                                                    <h3 className="wt-eyebrow">Projects</h3>
                                                    {projects.length > 0 && (
                                                        <span className="wt-index-count">{projects.length}</span>
                                                    )}
                                                </div>
                                                <div className="wt-index-scroll">
                                                    {renderProjectsList()}
                                                </div>
                                            </aside>
                                            <section className="wt-dossier" data-tour="wt-dossier">
                                                {selectedProject ? renderProjectDetail() : (
                                                    <div className="wt-empty-stage wt-empty-stage--quiet">
                                                        <span className="wt-empty-icon" aria-hidden="true"><FolderIcon block /></span>
                                                        <h3>Select a project</h3>
                                                        <p>Pick a project from the list to see its brief and files.</p>
                                                    </div>
                                                )}
                                            </section>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Group projects */}
                            {activeTab === 'group-projects' && groupData && (
                                <div
                                    className="wt-pane"
                                    role="tabpanel"
                                    id="wt-panel-group-projects"
                                    aria-labelledby="wt-tab-group-projects"
                                >
                                    {!groupLoading && !projectsLoading && groupProjects.length === 0 ? (
                                        <div className="wt-empty-stage">
                                            <span className="wt-empty-icon" aria-hidden="true"><FolderIcon block /></span>
                                            <h3>No group projects yet</h3>
                                            <p>{framing.groupProjectsEmpty}</p>
                                        </div>
                                    ) : (
                                        <div className={`wt-split ${selectedProject ? 'has-detail' : ''}`}>
                                            <aside className="wt-index" data-tour="wt-index">
                                                <div className="wt-index-head">
                                                    <h3 className="wt-eyebrow">Group projects</h3>
                                                    {groupProjects.length > 0 && (
                                                        <span className="wt-index-count">{groupProjects.length}</span>
                                                    )}
                                                </div>
                                                <div className="wt-index-scroll">
                                                    {renderProjectsList()}
                                                </div>
                                            </aside>
                                            <section className="wt-dossier" data-tour="wt-dossier">
                                                {selectedProject ? renderProjectDetail() : (
                                                    <div className="wt-empty-stage wt-empty-stage--quiet">
                                                        <span className="wt-empty-icon" aria-hidden="true"><FolderIcon block /></span>
                                                        <h3>Select a project</h3>
                                                        <p>Pick a group project from the list to see its brief and files.</p>
                                                    </div>
                                                )}
                                            </section>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Interviews */}
                            {activeTab === 'interviews' && (
                                <div
                                    className="wt-pane"
                                    role="tabpanel"
                                    id="wt-panel-interviews"
                                    aria-labelledby="wt-tab-interviews"
                                >
                                    {!interviewsLoading && interviews.length === 0 ? (
                                        <div className="wt-empty-stage">
                                            <span className="wt-empty-icon" aria-hidden="true"><QuestionIcon block /></span>
                                            <h3>No interviews yet</h3>
                                            <p>{framing.interviewsEmpty}</p>
                                        </div>
                                    ) : (
                                        <div className={`wt-split ${selectedInterview ? 'has-detail' : ''}`}>
                                            <aside className="wt-index" data-tour="wt-index">
                                                <div className="wt-index-head">
                                                    <h3 className="wt-eyebrow">Interviews</h3>
                                                    {interviews.length > 0 && (
                                                        <span className="wt-index-count">{interviews.length}</span>
                                                    )}
                                                </div>
                                                <div className="wt-index-scroll">
                                                    {renderInterviewsList()}
                                                </div>
                                            </aside>
                                            <section className="wt-dossier" data-tour="wt-dossier">
                                                {selectedInterview ? renderInterviewDetail() : (
                                                    <div className="wt-empty-stage wt-empty-stage--quiet">
                                                        <span className="wt-empty-icon" aria-hidden="true"><QuestionIcon block /></span>
                                                        <h3>Select an interview</h3>
                                                        <p>Pick an interview from the list to see its details.</p>
                                                    </div>
                                                )}
                                            </section>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Real-name warning (group chat) */}
            {showNameWarning && (
                <WtModal
                    titleId="wt-name-warning-title"
                    title="Reveal your real name?"
                    onClose={cancelNameWarning}
                    footer={
                        <>
                            <button type="button" className="wt-btn" onClick={cancelNameWarning}>
                                Don't send
                            </button>
                            <button type="button" className="wt-btn wt-btn--primary" onClick={confirmSendAnyway}>
                                Send anyway
                            </button>
                        </>
                    }
                >
                    <p className="wt-modal-text">
                        This message looks like it contains your real name. Sending it will reveal
                        your identity to the group.
                    </p>
                </WtModal>
            )}

            {/* First-visit tour for the active tab */}
            {activeTabTour && activeTabTour === activeTab && tabTourSteps && tabTourSteps.length > 0 && (
                <GuidedTour
                    steps={tabTourSteps}
                    onComplete={() => completeTabTour(activeTabTour)}
                />
            )}

            {/* Submission modal */}
            {showSubmissionModal && (() => {
                const isGroupMode = activeTab === 'group-projects' && groupData;
                const currentProjects = isGroupMode ? groupProjects : projects;
                const project = currentProjects.find(p => p.project_id === submissionProjectId);

                return (
                    <WtModal
                        titleId="wt-submit-title"
                        title="Submit project"
                        subtitle={project?.project_name}
                        onClose={handleCloseSubmissionModal}
                        closeDisabled={submitting}
                        footer={
                            <>
                                <button
                                    type="button"
                                    className="wt-btn"
                                    onClick={handleCloseSubmissionModal}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="wt-btn wt-btn--primary"
                                    onClick={handleSubmitProject}
                                    disabled={submitting || !submissionText.trim()}
                                >
                                    {submitting ? (
                                        <><span className="wt-spinner-sm" aria-hidden="true" /> Submitting…</>
                                    ) : (
                                        'Submit project'
                                    )}
                                </button>
                            </>
                        }
                    >
                        {project && (
                            <>
                                <div className="wt-field">
                                    <label className="wt-label" htmlFor="wt-submission-text">
                                        Your submission
                                    </label>
                                    <textarea
                                        id="wt-submission-text"
                                        className="wt-textarea"
                                        value={submissionText}
                                        onChange={(e) => setSubmissionText(e.target.value)}
                                        placeholder="Describe your work — your approach, findings, and anything the reviewers should know."
                                        disabled={submitting}
                                        required
                                    />
                                </div>

                                {project.max_files_needed > 0 && (
                                    <div className="wt-field">
                                        <label className="wt-label" htmlFor="wt-file-input">
                                            Files
                                            <span className="wt-label-hint">
                                                {submissionFiles.length > 0
                                                    ? `${submissionFiles.length} of ${project.max_files_needed} selected`
                                                    : `up to ${project.max_files_needed}`}
                                            </span>
                                        </label>

                                        <div
                                            className={`wt-upload ${dragOver ? 'is-dragover' : ''}`}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setDragOver(true);
                                            }}
                                            onDragLeave={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setDragOver(false);
                                            }}
                                            onDrop={handleFileDrop}
                                        >
                                            <input
                                                id="wt-file-input"
                                                type="file"
                                                multiple
                                                className="wt-upload-input"
                                                onChange={handleFileSelect}
                                                disabled={submitting}
                                                aria-label="Choose files to upload"
                                            />
                                            <div className="wt-upload-content">
                                                <span className="wt-upload-icon" aria-hidden="true"><UploadIcon /></span>
                                                <span className="wt-upload-text">Drop files here or click to browse</span>
                                                <span className="wt-upload-hint">Any file type</span>
                                            </div>
                                        </div>

                                        {submissionFiles.length > 0 && (
                                            <ul className="wt-file-list">
                                                {submissionFiles.map((file, index) => (
                                                    <li key={index} className="wt-file-row">
                                                        <span className="wt-file-icon" aria-hidden="true"><DocumentIcon /></span>
                                                        <span className="wt-file-name">{file.name}</span>
                                                        <span className="wt-file-size">{formatFileSize(file.size)}</span>
                                                        <button
                                                            type="button"
                                                            className="wt-icon-btn wt-icon-btn--small"
                                                            onClick={() => removeFile(index)}
                                                            disabled={submitting}
                                                            aria-label={`Remove ${file.name}`}
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </WtModal>
                );
            })()}

            {/* View submission modal */}
            {showViewSubmissionModal && viewingProject && (() => {
                const submission = readSubmission(viewingProject);

                return (
                <WtModal
                    titleId="wt-view-submission-title"
                    title="Your submission"
                    subtitle={`Submitted ${formatProjectDate(submission.submittedAt)}`}
                    onClose={handleCloseViewSubmissionModal}
                    footer={
                        <button
                            type="button"
                            className="wt-btn"
                            onClick={handleCloseViewSubmissionModal}
                        >
                            Close
                        </button>
                    }
                >
                    <div className="wt-field">
                        <span className="wt-label">Notes</span>
                        <div className="wt-readonly-text">
                            {submission.text || 'No submission text provided'}
                        </div>
                    </div>

                    <div className="wt-field">
                        <span className="wt-label">
                            Files
                            {submission.files?.length > 0 && (
                                <span className="wt-label-hint">{submission.files.length}</span>
                            )}
                        </span>
                        {submission.files?.length > 0 ? (
                            <ul className="wt-file-list">
                                {submission.files.map((fileName, index) => (
                                    <li key={index} className="wt-file-row">
                                        <span className="wt-file-icon" aria-hidden="true"><DocumentIcon /></span>
                                        <span className="wt-file-name">{fileName.split('/').pop()}</span>
                                        <button
                                            type="button"
                                            className="wt-btn wt-btn--small"
                                            onClick={() => handleDownloadSubmittedFile(fileName)}
                                        >
                                            Download
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : Array.isArray(submission.files) ? (
                            <div className="wt-readonly-text wt-readonly-text--muted">
                                No files were submitted with this project.
                            </div>
                        ) : (
                            /* The projects endpoint stopped emitting `submission_files`, so an
                               absent list means "not sent", not "none filed" — and saying the
                               second would tell a candidate their uploads are gone. The names
                               are the only missing piece: the download route they feed is still
                               live. Pending an owner ruling on re-emitting the list for trials
                               that have no version manifest to replace it. */
                            <div className="wt-readonly-text wt-readonly-text--muted">
                                The file list is not available in this view.
                            </div>
                        )}
                    </div>
                </WtModal>
                );
            })()}

            {/* GitHub setup modal */}
            {showGithubSetupModal && (
                <WtModal
                    titleId="wt-github-title"
                    title="Connect GitHub"
                    subtitle="This project uses a GitHub repository"
                    onClose={handleCloseGithubSetupModal}
                    closeDisabled={settingUpGithub}
                    footer={
                        <>
                            <button
                                type="button"
                                className="wt-btn"
                                onClick={handleCloseGithubSetupModal}
                                disabled={settingUpGithub}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="wt-btn wt-btn--primary"
                                onClick={handleSetupGithub}
                                disabled={settingUpGithub || !githubUsername.trim()}
                            >
                                {settingUpGithub ? (
                                    <><span className="wt-spinner-sm" aria-hidden="true" /> Connecting…</>
                                ) : (
                                    'Connect GitHub'
                                )}
                            </button>
                        </>
                    }
                >
                    <div className="wt-field">
                        <label className="wt-label" htmlFor="wt-github-username">
                            GitHub username
                        </label>
                        <input
                            id="wt-github-username"
                            type="text"
                            className="wt-input"
                            value={githubUsername}
                            onChange={(e) => setGithubUsername(e.target.value)}
                            placeholder="e.g. octocat"
                            disabled={settingUpGithub}
                            autoFocus
                        />
                        <p className="wt-help">Your exact username — access is granted to this account.</p>
                    </div>

                    <div className="wt-field">
                        <div className="wt-next-steps">
                            <span className="wt-next-steps-title">What happens next</span>
                            <ul>
                                <li>You're granted access to the project repository.</li>
                                <li>You can clone, push, and collaborate on the project.</li>
                                <li>Access is adjusted automatically at the project deadline.</li>
                            </ul>
                        </div>
                    </div>
                </WtModal>
            )}

            {/* Google Drive setup modal */}
            {showGmailSetupModal && (
                <WtModal
                    titleId="wt-gmail-title"
                    title="Connect Google Drive"
                    subtitle="Get access to the project's shared folder"
                    onClose={handleCloseGmailSetupModal}
                    closeDisabled={settingUpGmail}
                    footer={
                        <>
                            <button
                                type="button"
                                className="wt-btn"
                                onClick={handleCloseGmailSetupModal}
                                disabled={settingUpGmail}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="wt-btn wt-btn--primary"
                                onClick={handleSetupGmail}
                                disabled={settingUpGmail || !gmailInput.trim()}
                            >
                                {settingUpGmail ? (
                                    <><span className="wt-spinner-sm" aria-hidden="true" /> Connecting…</>
                                ) : (
                                    'Add email'
                                )}
                            </button>
                        </>
                    }
                >
                    <div className="wt-field">
                        <label className="wt-label" htmlFor="wt-gmail-input">
                            Email address
                        </label>
                        <input
                            id="wt-gmail-input"
                            type="email"
                            className="wt-input"
                            value={gmailInput}
                            onChange={(e) => setGmailInput(e.target.value)}
                            placeholder="you@gmail.com"
                            disabled={settingUpGmail}
                            autoFocus
                        />
                        <p className="wt-help">
                            Must be a Google account. If your Appli account email is already a Gmail,
                            you may already have access.
                        </p>
                    </div>

                    <div className="wt-field">
                        <div className="wt-next-steps">
                            <span className="wt-next-steps-title">What happens next</span>
                            <ul>
                                <li>You're granted access to the project's Google Drive.</li>
                                <li>You can create and edit documents directly in the folder.</li>
                                <li>Access is adjusted when the project is submitted.</li>
                                <li>You can remove this email later if needed.</li>
                            </ul>
                        </div>
                    </div>
                </WtModal>
            )}

            {/* Remove drive email confirmation */}
            {confirmRemoveGmailId && (
                <WtModal
                    titleId="wt-remove-email-title"
                    title="Remove your email?"
                    onClose={() => setConfirmRemoveGmailId(null)}
                    footer={
                        <>
                            <button
                                type="button"
                                className="wt-btn"
                                onClick={() => setConfirmRemoveGmailId(null)}
                            >
                                Keep access
                            </button>
                            <button
                                type="button"
                                className="wt-btn wt-btn--danger"
                                onClick={() => handleRemoveGmail(confirmRemoveGmailId)}
                            >
                                Remove email
                            </button>
                        </>
                    }
                >
                    <p className="wt-modal-text">
                        Your email will lose access to this project's Google Drive until you
                        add one again.
                    </p>
                </WtModal>
            )}

            {/* Toast stack — one polite live region, newest at the bottom */}
            <div className="wt-toasts" role="status" aria-live="polite">
                {toasts.map((toast) => (
                    <div key={toast.id} className={`wt-toast wt-toast--${toast.kind}`}>
                        <span className="wt-toast-icon" aria-hidden="true">
                            {toast.kind === 'success' ? <CheckIcon /> :
                             toast.kind === 'error' ? <WarningIcon /> : <InfoIcon />}
                        </span>
                        <span className="wt-toast-text">{toast.text}</span>
                        <button
                            type="button"
                            className="wt-icon-btn wt-icon-btn--small wt-toast-dismiss"
                            onClick={() => dismissToast(toast.id)}
                            aria-label="Dismiss notification"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                ))}
            </div>
        </>
    );
};

export default WorkTrial; 